function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH21_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── V1 — Campaign/Ad Build (3) ───────────────────────────────────────

  "Ad Copy Generator": {
    nodes: [
      trigger("Manual trigger: Marketer submits campaign brief", "manual", {
        howItStarts:
          "Marketer submits a campaign brief with product details, audience description, campaign objective, desired CTA, and any copy restrictions.",
        setup:
          "Create this workflow and trigger it manually when a new campaign brief is ready for ad copy generation.",
        inputFields: [
          {
            name: "campaign_name",
            type: "string",
            example: "Summer Sale 2025",
          },
          {
            name: "product_description",
            type: "string",
            example: "Wireless noise-canceling headphones with 30-hour battery",
          },
          {
            name: "target_audience",
            type: "string",
            example: "Tech-savvy professionals aged 25-45",
          },
          {
            name: "campaign_objective",
            type: "string",
            example: "Drive online purchases with 3x ROAS",
          },
          {
            name: "desired_cta",
            type: "string",
            example: "Shop Now — Free Shipping",
          },
          {
            name: "copy_restrictions",
            type: "string",
            example: "No superlatives, must include price point",
          },
        ],
      }),
      aiNode(
        "2",
        "AI: Extract campaign objective, audience, product, CTA, restrictions",
        280,
        "You are a campaign brief analyst. Parse the raw campaign brief and extract structured fields: campaign_objective, audience_segments (array), product_features (array), primary_cta, secondary_cta, restrictions (array), tone_of_voice, and key_value_propositions (array).",
        "Parse this campaign brief and extract structured fields: Campaign: {campaign_name}. Product: {product_description}. Audience: {target_audience}. Objective: {campaign_objective}. CTA: {desired_cta}. Restrictions: {copy_restrictions}.",
        [
          {
            field: "campaign_objective",
            as: "campaign_objective",
            type: "string",
          },
          {
            field: "audience_segments",
            as: "audience_segments",
            type: "object",
          },
          { field: "product_features", as: "product_features", type: "object" },
          { field: "primary_cta", as: "primary_cta", type: "string" },
          { field: "restrictions", as: "restrictions", type: "object" },
          { field: "tone_of_voice", as: "tone_of_voice", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI: Research competitor positioning and platform best practices",
        520,
        "You are an advertising strategist. Given the product category and audience, research competitor messaging patterns and platform-specific ad copy best practices. Return competitor_themes (array of common competitor messaging angles), platform_tips (object with Google, Facebook, TikTok tips), and differentiation_opportunities (array).",
        "Product features: {product_features}. Audience: {audience_segments}. Objective: {campaign_objective}. Analyze competitor positioning and platform best practices for this category.",
        [
          {
            field: "competitor_themes",
            as: "competitor_themes",
            type: "object",
          },
          { field: "platform_tips", as: "platform_tips", type: "object" },
          {
            field: "differentiation_opportunities",
            as: "differentiation_opportunities",
            type: "object",
          },
        ],
      ),
      aiNode(
        "4",
        "AI: Generate 5 ad copy variants with headlines, descriptions, CTAs",
        760,
        "You are an expert ad copywriter. Generate exactly 5 ad copy variants. Each variant must include: headline (short), description (medium), cta_text, platform_target (Google/Facebook/TikTok), emotional_hook, and unique_angle. Incorporate competitor differentiation and platform best practices. Respect all copy restrictions.",
        "Campaign: {campaign_objective}. Product: {product_features}. Audience: {audience_segments}. CTA: {primary_cta}. Restrictions: {restrictions}. Tone: {tone_of_voice}. Competitor themes to differentiate from: {competitor_themes}. Platform tips: {platform_tips}. Generate 5 ad copy variants.",
        [
          { field: "variants", as: "ad_variants", type: "object" },
          {
            field: "creative_rationale",
            as: "creative_rationale",
            type: "string",
          },
        ],
      ),
      n(
        "5",
        "runCode",
        "Validate character limits by platform (Google: 30/90, Facebook: 40/125)",
        1000,
        {
          runCodeDetails: {
            code: `const variants = input.ad_variants || [];
const limits = { Google: { headline: 30, description: 90 }, Facebook: { headline: 40, description: 125 }, TikTok: { headline: 50, description: 150 } };
const violations = [];
const validated = variants.map((v, i) => {
  const platform = v.platform_target || "Facebook";
  const limit = limits[platform] || limits.Facebook;
  const headlineOver = v.headline.length > limit.headline;
  const descOver = v.description.length > limit.description;
  if (headlineOver || descOver) violations.push({ variant: i + 1, platform, headlineLen: v.headline.length, descLen: v.description.length, headlineLimit: limit.headline, descLimit: limit.description });
  return { ...v, headline_length: v.headline.length, description_length: v.description.length, within_limits: !headlineOver && !descOver };
});
return { validated_variants: validated, violations, any_violations: violations.length > 0, total_variants: variants.length };`,
          },
        },
      ),
      n("6", "condition", "Any variants exceed character limits?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "any_violations", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "7",
        "approvalGate",
        "Marketing owner reviews ad copy variants",
        1480,
        {
          approvalDetails: {
            approver: "marketing-owner",
            timeout: 72,
            timeoutUnit: "hours",
          },
        },
        80,
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log approved variants with test structure",
        1720,
        "Approved_Ad_Copy",
        '[["{campaign_name}","{ad_variants[0].headline}","{ad_variants[0].description}","{ad_variants[0].cta_text}","{ad_variants[0].platform_target}","{creative_rationale}","{tokens_used}","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Generates production-ready ad copy from a campaign brief. Extracts objectives and constraints, researches competitor positioning, produces 5 platform-specific ad variants with headlines and CTAs, validates character limits per platform (Google, Facebook, TikTok), routes through marketing approval, and logs approved variants to Google Sheets for A/B test tracking.",
  },

  "Facebook Ad Campaign Builder": {
    nodes: [
      trigger("Manual trigger: Media buyer submits campaign brief", "manual", {
        howItStarts:
          "A media buyer submits a Facebook campaign brief with objectives, target audiences, budget, and creative requirements.",
        setup:
          "Configure this workflow and trigger it manually when ready to build a new Facebook ad campaign.",
        inputFields: [
          {
            name: "campaign_name",
            type: "string",
            example: "Q3 Retargeting — High-Intent Visitors",
          },
          {
            name: "campaign_objective",
            type: "string",
            example: "CONVERSIONS",
          },
          { name: "daily_budget", type: "number", example: "150" },
          {
            name: "target_audience_description",
            type: "string",
            example: "Website visitors who viewed pricing page in last 14 days",
          },
          {
            name: "ad_creative_brief",
            type: "string",
            example: "Social proof focused, customer testimonials, urgency CTA",
          },
          { name: "start_date", type: "string", example: "2025-08-01" },
          { name: "end_date", type: "string", example: "2025-08-31" },
        ],
      }),
      aiNode(
        "2",
        "AI: Plan campaign structure — objectives, audience segments, budget splits",
        280,
        "You are a Facebook Ads strategist. Given a campaign brief, produce a structured campaign plan: campaign_objective (AWARENESS/TRAFFIC/ENGAGEMENT/LEADS/APP_PROMOTION/SALES), audience_segments (array of targeting objects with age, gender, interests, custom audiences, lookalikes), budget_allocation (object with per-segment daily amounts), bid_strategy, and optimization_event.",
        "Campaign: {campaign_name}. Objective: {campaign_objective}. Daily budget: ${daily_budget}. Audience: {target_audience_description}. Creative: {ad_creative_brief}. Dates: {start_date} to {end_date}. Plan the full campaign structure.",
        [
          { field: "campaign_plan", as: "campaign_plan", type: "object" },
          {
            field: "audience_segments",
            as: "audience_segments",
            type: "object",
          },
          {
            field: "budget_allocation",
            as: "budget_allocation",
            type: "object",
          },
          { field: "bid_strategy", as: "bid_strategy", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Facebook Marketing API: Create campaign with objective",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/campaigns",
            headers: [
              { key: "Authorization", value: "Bearer {facebook_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "name", value: "{campaign_name}" },
              { key: "objective", value: "{campaign_objective}" },
              { key: "status", value: "PAUSED" },
              { key: "special_ad_categories", value: "[]" },
              { key: "buying_type", value: "AUCTION" },
            ],
            variables: [
              { name: "id", customName: "campaign_id", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Facebook API: Create ad sets with targeting and budget",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/adsets",
            headers: [
              { key: "Authorization", value: "Bearer {facebook_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "campaign_id", value: "{campaign_id}" },
              { key: "name", value: "{campaign_name} — Ad Set 1" },
              { key: "daily_budget", value: "{daily_budget}00" },
              { key: "billing_event", value: "IMPRESSIONS" },
              { key: "optimization_goal", value: "OFFSITE_CONVERSIONS" },
              { key: "bid_strategy", value: "{bid_strategy}" },
              { key: "targeting", value: "{audience_segments[0]}" },
              { key: "start_time", value: "{start_date}T00:00:00-0000" },
              { key: "end_time", value: "{end_date}T23:59:59-0000" },
              { key: "status", value: "PAUSED" },
            ],
            variables: [{ name: "id", customName: "adset_id", type: "string" }],
          },
        },
      ),
      aiNode(
        "5",
        "AI: Generate ad creative copy variants for campaign",
        1000,
        "You are a Facebook ad copywriter. Generate 3 ad creative variants for the campaign. Each variant must include: primary_text (max 125 chars), headline (max 40 chars), description (max 30 chars), call_to_action (LEARN_MORE/SHOP_NOW/SIGN_UP/BOOK_NOW), and creative_angle.",
        "Campaign: {campaign_name}. Objective: {campaign_objective}. Creative brief: {ad_creative_brief}. Audience: {target_audience_description}. Generate 3 ad creative variants.",
        [
          { field: "ad_creatives", as: "ad_creatives", type: "object" },
          {
            field: "creative_strategy",
            as: "creative_strategy",
            type: "string",
          },
        ],
      ),
      n(
        "6",
        "runCode",
        "Validate ad specs against Meta advertising policies",
        1240,
        {
          runCodeDetails: {
            code: `const creatives = input.ad_creatives || [];
const violations = [];
creatives.forEach((c, i) => {
  if (c.primary_text && c.primary_text.length > 125) violations.push({ variant: i + 1, issue: "Primary text exceeds 125 chars", length: c.primary_text.length });
  if (c.headline && c.headline.length > 40) violations.push({ variant: i + 1, issue: "Headline exceeds 40 chars", length: c.headline.length });
  if (c.description && c.description.length > 30) violations.push({ variant: i + 1, issue: "Description exceeds 30 chars", length: c.description.length });
  const prohibited = ["guaranteed", "100%", "#1"];
  prohibited.forEach(word => {
    if ((c.primary_text || "").toLowerCase().includes(word)) violations.push({ variant: i + 1, issue: "Contains prohibited term: " + word });
  });
});
return { validated_creatives: creatives, violations, is_compliant: violations.length === 0, creative_count: creatives.length };`,
          },
        },
      ),
      n(
        "7",
        "approvalGate",
        "Media buyer approves campaign before launch",
        1480,
        {
          approvalDetails: {
            approver: "media-buyer",
            timeout: 48,
            timeoutUnit: "hours",
          },
        },
      ),
      n(
        "8",
        "hitApi",
        "Facebook API: Launch campaign with limited initial spend",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://graph.facebook.com/v19.0/{campaign_id}",
            headers: [
              { key: "Authorization", value: "Bearer {facebook_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "status", value: "ACTIVE" }],
            variables: [
              {
                name: "success",
                customName: "campaign_launched",
                type: "boolean",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "9",
        "Google Sheets: Log campaign setup for performance tracking",
        1960,
        "Campaign_Tracker",
        '[["{campaign_name}","{campaign_id}","{adset_id}","{campaign_objective}","{daily_budget}","{bid_strategy}","{start_date}","{end_date}","ACTIVE","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "End-to-end Facebook ad campaign builder. Plans campaign structure from a brief, creates campaigns and ad sets via the Facebook Marketing API with targeting and budget, generates ad creative variants, validates against Meta policies, routes through media buyer approval, launches with controlled initial spend, and logs the full setup to Google Sheets for performance tracking.",
  },

  "TikTok Video Ad Pipeline": {
    nodes: [
      trigger("Manual trigger: Creative team submits video brief", "manual", {
        howItStarts:
          "A creative team member submits a TikTok video ad brief with product details, target audience, desired style, and campaign budget.",
        setup:
          "Configure this workflow and trigger it manually when a new TikTok video ad needs to be produced and launched.",
        inputFields: [
          {
            name: "video_brief",
            type: "string",
            example:
              "15-second product demo showcasing wireless earbuds in urban setting",
          },
          { name: "product_name", type: "string", example: "AirPods Pro 3" },
          {
            name: "target_audience",
            type: "string",
            example: "Gen Z music lovers, 18-25, urban",
          },
          { name: "campaign_budget", type: "number", example: "8000" },
          {
            name: "preferred_style",
            type: "string",
            example: "UGC-style, trending sound, fast cuts",
          },
          { name: "campaign_duration_days", type: "number", example: "14" },
        ],
      }),
      aiNode(
        "2",
        "AI: Generate video script and storyboard from brief",
        280,
        "You are a TikTok creative strategist. Generate a detailed video script and storyboard for a TikTok ad. Include: hook (first 3 seconds), scene_breakdown (array of scenes with timing, visuals, text overlay, audio cue), cta_scene, total_duration, recommended_sounds (trending), and visual_style_notes.",
        "Video brief: {video_brief}. Product: {product_name}. Audience: {target_audience}. Style: {preferred_style}. Duration: 15 seconds. Generate the full script and storyboard.",
        [
          { field: "script", as: "video_script", type: "object" },
          { field: "storyboard", as: "storyboard_scenes", type: "object" },
          {
            field: "recommended_sounds",
            as: "recommended_sounds",
            type: "object",
          },
          { field: "total_duration", as: "total_duration", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Create production task checklist with deadlines",
        520,
        {
          runCodeDetails: {
            code: `const scenes = input.storyboard_scenes || [];
const now = new Date();
const tasks = [
  { task: "Source trending audio tracks", assignee: "sound-designer", due: new Date(now.getTime() + 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Film scenes: " + scenes.length + " shots", assignee: "videographer", due: new Date(now.getTime() + 3 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Edit video with transitions and text overlays", assignee: "editor", due: new Date(now.getTime() + 5 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Add captions and accessibility text", assignee: "editor", due: new Date(now.getTime() + 6 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Create 3 thumbnail options", assignee: "designer", due: new Date(now.getTime() + 5 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Final review and export in 9:16 format", assignee: "creative-lead", due: new Date(now.getTime() + 7 * 86400000).toISOString().split("T")[0], status: "pending" },
];
return { production_tasks: tasks, total_tasks: tasks.length, estimated_completion: tasks[tasks.length - 1].due };`,
          },
        },
      ),
      n("4", "approvalGate", "Creative director approves storyboard", 760, {
        approvalDetails: {
          approver: "creative-director",
          timeout: 48,
          timeoutUnit: "hours",
        },
      }),
      slackNode(
        "5",
        "Slack: Notify production team with approved brief and tasks",
        1000,
        "🎬 *TikTok Video Ad — Production Approved*\\n\\n*Product:* {product_name}\\n*Style:* {preferred_style}\\n*Duration:* {total_duration}\\n*Tasks:* {total_tasks} production tasks created\\n*Estimated Completion:* {estimated_completion}\\n\\n*Script Hook:* {video_script.hook}\\n*Scenes:* {storyboard_scenes.length} shots\\n\\nCheck your task assignments and begin production.",
      ),
      n("6", "hitApi", "TikTok Ads API: Create campaign with targeting", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://business-api.tiktok.com/open_api/v1.3/campaign/create/",
          headers: [
            { key: "Access-Token", value: "{tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advertiser_id", value: "{tiktok_advertiser_id}" },
            { key: "campaign_name", value: "{product_name} — TikTok Video Ad" },
            { key: "objective_type", value: "VIDEO_VIEWS" },
            { key: "budget_mode", value: "BUDGET_MODE_TOTAL" },
            { key: "budget", value: "{campaign_budget}" },
          ],
          variables: [
            {
              name: "data.campaign_id",
              customName: "tiktok_campaign_id",
              type: "string",
            },
          ],
        },
      }),
      n("7", "condition", "Budget exceeds $5,000?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "campaign_budget",
              operator: "greater_than",
              value: "5000",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log pipeline status and production milestones",
        1720,
        "TikTok_Pipeline",
        '[["{product_name}","{tiktok_campaign_id}","{campaign_budget}","{preferred_style}","{total_tasks}","{estimated_completion}","IN_PRODUCTION","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Full TikTok video ad production and launch pipeline. Generates a video script and storyboard from a creative brief, builds a production task checklist with deadlines, routes through creative director approval, notifies the production team via Slack, creates the TikTok Ads campaign via API, flags high-budget campaigns for extra review, and tracks the entire pipeline in Google Sheets.",
  },

  // ─── V2 — Performance Monitoring (4) ──────────────────────────────────

  "Ad Creative Fatigue Detector": {
    nodes: [
      trigger("Schedule: Daily at 7 AM — check creative fatigue", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs automatically every day at 7 AM to scan all active ad creatives for performance fatigue signals.",
        setup:
          "Connect your Facebook Ads account and configure alert thresholds. The workflow will monitor creative-level metrics daily.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Facebook Marketing API: Fetch creative-level performance metrics",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/insights?fields=ad_id,ad_name,impressions,clicks,ctr,frequency,spend,actions,cost_per_action_type&level=ad&date_preset=last_7d&limit=500",
            headers: [
              { key: "Authorization", value: "Bearer {facebook_access_token}" },
            ],
            body: [],
            variables: [
              { name: "data", customName: "creative_metrics", type: "object" },
              { name: "paging.next", customName: "next_page", type: "string" },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute per-creative baseline and detect fatigue signals",
        520,
        {
          runCodeDetails: {
            code: `const metrics = input.creative_metrics || [];
const fatigued = [];
metrics.forEach(m => {
  const ctr = parseFloat(m.ctr || "0");
  const frequency = parseFloat(m.frequency || "0");
  const impressions = parseInt(m.impressions || "0");
  const signals = [];
  if (ctr < 0.8 && impressions > 1000) signals.push("CTR below 0.8% threshold");
  if (frequency > 4) signals.push("Frequency exceeds 4x — audience oversaturation");
  if (ctr < 1.0 && frequency > 3) signals.push("CTR declining with rising frequency — classic fatigue pattern");
  if (signals.length > 0) {
    fatigued.push({ ad_id: m.ad_id, ad_name: m.ad_name, ctr, frequency, impressions: parseInt(m.impressions), spend: m.spend, fatigue_signals: signals, severity: signals.length >= 2 ? "high" : "medium" });
  }
});
return { fatigued_creatives: fatigued, fatigue_count: fatigued.length, any_fatigue: fatigued.length > 0, total_creatives_scanned: metrics.length };`,
          },
        },
      ),
      n("4", "condition", "Any creatives showing fatigue?", 760, {
        conditionDetails: {
          conditions: [
            { field: "any_fatigue", operator: "equals", value: "true" },
          ],
        },
      }),
      aiNode(
        "5",
        "AI: Analyze fatigue patterns and recommend refresh strategy",
        1000,
        "You are a performance marketing analyst. Analyze the fatigued ad creatives and provide: fatigue_summary (overview of patterns), refresh_recommendations (array with specific actions per creative: pause, refresh copy, swap creative, retarget), priority_actions (top 3 immediate actions), and estimated_recovery_timeline.",
        "Fatigued creatives: {fatigued_creatives}. Total scanned: {total_creatives_scanned}. Analyze patterns and recommend refresh strategies for each fatigued creative.",
        [
          { field: "fatigue_summary", as: "fatigue_summary", type: "string" },
          {
            field: "refresh_recommendations",
            as: "refresh_recommendations",
            type: "object",
          },
          { field: "priority_actions", as: "priority_actions", type: "object" },
        ],
      ),
      slackNode(
        "6",
        "Slack: Alert media buyer with fatigued creatives and recommendations",
        1240,
        "⚠️ *Ad Creative Fatigue Alert*\\n\\n*Fatigued Creatives:* {fatigue_count} of {total_creatives_scanned} scanned\\n\\n*Summary:* {fatigue_summary}\\n\\n*Priority Actions:*\\n{priority_actions}\\n\\nReview the full refresh recommendations in the tracker sheet.",
      ),
      emailNode(
        "7",
        "Email: Weekly creative health report",
        1480,
        "{media_buyer_email}",
        "Weekly Creative Health Report — {fatigue_count} Creatives Need Attention",
        "Hi,\\n\\nYour weekly creative health scan detected {fatigue_count} fatigued creatives out of {total_creatives_scanned} total.\\n\\n{fatigue_summary}\\n\\nPriority Actions:\\n{priority_actions}\\n\\nFull refresh recommendations and performance data are available in the tracking spreadsheet.\\n\\nBest,\\nOmniEngage Ad Monitor",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Track creative lifecycle metrics",
        1720,
        "Creative_Fatigue_Log",
        '[["{new Date().toISOString().split(\'T\')[0]}","{fatigue_count}","{total_creatives_scanned}","{fatigue_summary}","{priority_actions[0]}","{priority_actions[1]}","{priority_actions[2]}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily creative fatigue monitoring system. Fetches creative-level performance metrics from Facebook Marketing API, computes fatigue signals (CTR decline, frequency saturation, engagement drops), uses AI to analyze patterns and recommend refresh strategies, alerts the media buyer via Slack and email, and tracks creative lifecycle metrics in Google Sheets for trend analysis.",
  },

  "Ad Performance Dashboard": {
    nodes: [
      trigger(
        "Schedule: Daily at 6 AM — aggregate cross-platform metrics",
        "schedule",
        {
          schedule: "0 6 * * *",
          howItStarts:
            "Runs every day at 6 AM to pull performance data from Facebook and Google Ads, normalize it, and generate a unified dashboard.",
          setup:
            "Connect both Facebook Ads and Google Ads API credentials. Configure the Google Sheets dashboard template with proper column headers.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Facebook Marketing API: Fetch campaign performance",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/insights?fields=campaign_name,spend,impressions,clicks,ctr,actions,cost_per_action_type,purchase_roas&date_preset=yesterday&level=campaign&limit=100",
            headers: [
              { key: "Authorization", value: "Bearer {facebook_access_token}" },
            ],
            body: [],
            variables: [
              { name: "data", customName: "facebook_metrics", type: "object" },
            ],
          },
        },
      ),
      n("3", "hitApi", "Google Ads API: Fetch campaign performance", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, metrics.cost_micros, metrics.impressions, metrics.clicks, metrics.ctr, metrics.conversions, metrics.cost_per_conversion FROM campaign WHERE segments.date DURING YESTERDAY AND campaign.status = 'ENABLED'",
            },
          ],
          variables: [
            { name: "results", customName: "google_metrics", type: "object" },
          ],
        },
      }),
      n(
        "4",
        "runCode",
        "Normalize cross-platform metrics: spend, impressions, clicks, conversions, ROAS",
        760,
        {
          runCodeDetails: {
            code: `const fb = input.facebook_metrics || [];
const goog = input.google_metrics || [];
const normalized = [];
fb.forEach(m => {
  const conversions = (m.actions || []).find(a => a.action_type === "purchase");
  const roas = (m.purchase_roas || [{}])[0].value || 0;
  normalized.push({ platform: "Facebook", campaign: m.campaign_name, spend: parseFloat(m.spend || "0"), impressions: parseInt(m.impressions || "0"), clicks: parseInt(m.clicks || "0"), ctr: parseFloat(m.ctr || "0"), conversions: conversions ? parseInt(conversions.value) : 0, roas: parseFloat(roas) });
});
goog.forEach(r => {
  const m = r.metrics || {};
  const c = r.campaign || {};
  normalized.push({ platform: "Google", campaign: c.name, spend: (parseInt(m.cost_micros || "0")) / 1000000, impressions: parseInt(m.impressions || "0"), clicks: parseInt(m.clicks || "0"), ctr: parseFloat(m.ctr || "0"), conversions: parseFloat(m.conversions || "0"), roas: m.cost_per_conversion > 0 ? (parseFloat(m.conversions) * 50) / (parseInt(m.cost_micros) / 1000000) : 0 });
});
const totalSpend = normalized.reduce((s, r) => s + r.spend, 0);
const totalConversions = normalized.reduce((s, r) => s + r.conversions, 0);
const overallROAS = totalSpend > 0 ? (totalConversions * 50) / totalSpend : 0;
return { normalized_metrics: normalized, total_spend: totalSpend.toFixed(2), total_conversions: totalConversions, overall_roas: overallROAS.toFixed(2), campaign_count: normalized.length, date: new Date().toISOString().split("T")[0] };`,
          },
        },
      ),
      aiNode(
        "5",
        "AI: Generate narrative weekly performance summary",
        1000,
        "You are a performance marketing analyst. Generate a concise narrative performance summary from the normalized campaign metrics. Include: executive_summary (2-3 sentences), top_performers (top 3 campaigns by ROAS), underperformers (campaigns below 1.0 ROAS), spend_efficiency_rating (A/B/C/D), and recommendations (array of 3 specific actions).",
        "Date: {date}. Total spend: ${total_spend}. Total conversions: {total_conversions}. Overall ROAS: {overall_roas}x. Campaigns: {normalized_metrics}. Generate a narrative performance summary.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "top_performers", as: "top_performers", type: "object" },
          { field: "underperformers", as: "underperformers", type: "object" },
          { field: "recommendations", as: "recommendations", type: "object" },
        ],
      ),
      sheetsNode(
        "6",
        "Google Sheets: Update dashboard with current metrics",
        1240,
        "Performance_Dashboard",
        '[["{date}","{total_spend}","{total_conversions}","{overall_roas}","{campaign_count}","{executive_summary}","{recommendations[0]}","{recommendations[1]}"]]',
      ),
      slackNode(
        "7",
        "Slack: Post daily performance snapshot",
        1480,
        "📊 *Daily Ad Performance — {date}*\\n\\n*Total Spend:* ${total_spend}\\n*Conversions:* {total_conversions}\\n*Overall ROAS:* {overall_roas}x\\n*Campaigns:* {campaign_count}\\n\\n*Summary:* {executive_summary}\\n\\n*Top Recommendations:*\\n• {recommendations[0]}\\n• {recommendations[1]}\\n• {recommendations[2]}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Unified cross-platform ad performance dashboard. Pulls campaign metrics from both Facebook Marketing API and Google Ads API daily, normalizes data into a common schema (spend, impressions, clicks, conversions, ROAS), generates an AI-powered narrative summary with top performers and recommendations, updates the Google Sheets dashboard, and posts a daily snapshot to Slack.",
  },

  "Google Ads Performance Alert": {
    nodes: [
      trigger(
        "Schedule: Every 4 hours — monitor Google Ads performance",
        "schedule",
        {
          schedule: "0 */4 * * *",
          howItStarts:
            "Runs every 4 hours to check Google Ads campaign performance against defined alert thresholds.",
          setup:
            "Connect your Google Ads API credentials and configure alert thresholds for CPC, quality score, and conversion rate in the runCode node.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Google Ads API: Fetch campaign and ad group performance",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v16/customers/{google_customer_id}/googleAds:searchStream",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "developer-token", value: "{google_developer_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  "SELECT campaign.name, ad_group.name, metrics.cost_micros, metrics.clicks, metrics.impressions, metrics.ctr, metrics.average_cpc, metrics.conversions, metrics.cost_per_conversion, ad_group_criterion.quality_info.quality_score FROM ad_group WHERE segments.date DURING TODAY AND campaign.status = 'ENABLED'",
              },
            ],
            variables: [
              {
                name: "results",
                customName: "ad_group_metrics",
                type: "object",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Compare metrics against alert thresholds", 520, {
        runCodeDetails: {
          code: `const metrics = input.ad_group_metrics || [];
const thresholds = { max_cpc: 3.50, min_quality_score: 5, max_conversion_rate_drop: 15, min_ctr: 1.5 };
const alerts = [];
metrics.forEach(r => {
  const m = r.metrics || {};
  const campaign = (r.campaign || {}).name || "Unknown";
  const adGroup = (r.ad_group || {}).name || "Unknown";
  const cpc = (parseInt(m.average_cpc || "0")) / 1000000;
  const qualityScore = (r.ad_group_criterion || {}).quality_info ? r.ad_group_criterion.quality_info.quality_score : null;
  const ctr = parseFloat(m.ctr || "0") * 100;
  const conversions = parseFloat(m.conversions || "0");
  const clicks = parseInt(m.clicks || "0");
  const convRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
  const issues = [];
  if (cpc > thresholds.max_cpc) issues.push("CPC $" + cpc.toFixed(2) + " exceeds $" + thresholds.max_cpc + " target");
  if (qualityScore !== null && qualityScore < thresholds.min_quality_score) issues.push("Quality score " + qualityScore + " below minimum " + thresholds.min_quality_score);
  if (ctr < thresholds.min_ctr && parseInt(m.impressions || "0") > 100) issues.push("CTR " + ctr.toFixed(1) + "% below " + thresholds.min_ctr + "% threshold");
  if (issues.length > 0) alerts.push({ campaign, ad_group: adGroup, cpc: cpc.toFixed(2), quality_score: qualityScore, ctr: ctr.toFixed(1), conversion_rate: convRate.toFixed(1), issues, severity: issues.length >= 2 ? "critical" : "warning" });
});
return { alerts, alert_count: alerts.length, any_alerts: alerts.length > 0, total_ad_groups: metrics.length, check_time: new Date().toISOString() };`,
        },
      }),
      n("4", "condition", "Any alerts triggered?", 760, {
        conditionDetails: {
          conditions: [
            { field: "any_alerts", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Send urgent alert with affected campaigns and actions",
        1000,
        "🚨 *Google Ads Performance Alert*\\n\\n*{alert_count} issues detected* across {total_ad_groups} ad groups\\n\\n*Critical Alerts:*\\n{alerts[0].campaign} / {alerts[0].ad_group}:\\n• {alerts[0].issues[0]}\\n\\n*Recommended Actions:*\\n• Review bid strategy for high-CPC ad groups\\n• Pause underperforming keywords with quality score < 5\\n• Refresh ad copy for low-CTR groups\\n\\nCheck time: {check_time}",
      ),
      emailNode(
        "6",
        "Email: Daily performance summary to SEM team",
        1240,
        "{sem_team_email}",
        "Google Ads Performance Summary — {alert_count} Alerts",
        "Team,\\n\\nPerformance check at {check_time} found {alert_count} alerts across {total_ad_groups} ad groups.\\n\\nAlert Details:\\n{alerts}\\n\\nPlease review and take action on critical alerts immediately.\\n\\nBest,\\nOmniEngage Google Ads Monitor",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log alert history and actions taken",
        1480,
        "Google_Ads_Alerts",
        '[["{check_time}","{alert_count}","{total_ad_groups}","{alerts[0].campaign}","{alerts[0].severity}","{alerts[0].issues[0]}","pending"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Proactive Google Ads performance monitoring system. Runs every 4 hours to fetch campaign and ad group metrics, compares against configurable thresholds (CPC targets, quality score minimums, CTR floors), sends urgent Slack alerts with specific recommendations when issues are detected, emails the SEM team with daily summaries, and logs alert history to Google Sheets.",
  },

  "YouTube Channel Analyzer Agent": {
    nodes: [
      trigger("Schedule: Weekly on Mondays — channel analysis", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday morning to analyze YouTube channel performance, benchmark against competitors, and generate strategic recommendations.",
        setup:
          "Configure your YouTube Data API key, your channel ID, and competitor channel IDs in the API nodes.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "YouTube Data API: Fetch channel analytics — views, subscribers, engagement",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/youtube/v3/channels?part=statistics,snippet,contentDetails&id={youtube_channel_id}&key={youtube_api_key}",
            headers: [],
            body: [],
            variables: [
              {
                name: "items[0].statistics",
                customName: "channel_stats",
                type: "object",
              },
              {
                name: "items[0].snippet.title",
                customName: "channel_name",
                type: "string",
              },
              {
                name: "items[0].contentDetails.relatedPlaylists.uploads",
                customName: "uploads_playlist",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "YouTube API: Fetch competitor channel stats for benchmarking",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/youtube/v3/channels?part=statistics,snippet&id={competitor_channel_id_1},{competitor_channel_id_2},{competitor_channel_id_3}&key={youtube_api_key}",
            headers: [],
            body: [],
            variables: [
              {
                name: "items",
                customName: "competitor_channels",
                type: "object",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "AI: Analyze content performance patterns and category mapping",
        760,
        "You are a YouTube analytics expert. Compare the channel performance against competitors. Analyze: content_categories (what types of videos perform best), posting_frequency_vs_competitors, subscriber_growth_comparison, engagement_rate_comparison, and content_gaps (topics competitors cover that this channel does not). Return structured analysis.",
        "Channel: {channel_name}. Stats: {channel_stats}. Competitors: {competitor_channels}. Analyze content performance patterns, category mapping, and competitive positioning.",
        [
          {
            field: "content_categories",
            as: "content_categories",
            type: "object",
          },
          {
            field: "competitive_analysis",
            as: "competitive_analysis",
            type: "object",
          },
          { field: "content_gaps", as: "content_gaps", type: "object" },
          {
            field: "engagement_comparison",
            as: "engagement_comparison",
            type: "string",
          },
        ],
      ),
      n(
        "5",
        "runCode",
        "Compute key metrics: audience retention, CTR, growth rate",
        1000,
        {
          runCodeDetails: {
            code: `const stats = input.channel_stats || {};
const competitors = input.competitor_channels || [];
const totalViews = parseInt(stats.viewCount || "0");
const subscribers = parseInt(stats.subscriberCount || "0");
const videoCount = parseInt(stats.videoCount || "0");
const avgViewsPerVideo = videoCount > 0 ? Math.round(totalViews / videoCount) : 0;
const engagementRate = totalViews > 0 ? ((parseInt(stats.commentCount || "0") + parseInt(stats.likeCount || "0")) / totalViews * 100).toFixed(2) : "0";
const competitorBenchmarks = competitors.map(c => ({
  name: c.snippet.title,
  subscribers: parseInt(c.statistics.subscriberCount || "0"),
  views: parseInt(c.statistics.viewCount || "0"),
  videos: parseInt(c.statistics.videoCount || "0"),
  avg_views: parseInt(c.statistics.videoCount) > 0 ? Math.round(parseInt(c.statistics.viewCount) / parseInt(c.statistics.videoCount)) : 0,
}));
return { channel_metrics: { total_views: totalViews, subscribers, video_count: videoCount, avg_views_per_video: avgViewsPerVideo, engagement_rate: engagementRate + "%" }, competitor_benchmarks: competitorBenchmarks, analysis_date: new Date().toISOString().split("T")[0] };`,
          },
        },
      ),
      aiNode(
        "6",
        "AI: Generate strategic recommendations for channel growth",
        1240,
        "You are a YouTube growth strategist. Based on the channel metrics, competitor benchmarks, content analysis, and content gaps, provide: strategic_recommendations (array of 5 specific actionable recommendations), content_calendar_suggestions (next 4 video topics with rationale), optimization_tips (thumbnail, title, description improvements), and growth_forecast (realistic 90-day projection).",
        "Channel metrics: {channel_metrics}. Competitor benchmarks: {competitor_benchmarks}. Content categories: {content_categories}. Content gaps: {content_gaps}. Competitive analysis: {competitive_analysis}. Generate strategic recommendations.",
        [
          {
            field: "strategic_recommendations",
            as: "strategic_recommendations",
            type: "object",
          },
          {
            field: "content_calendar_suggestions",
            as: "content_calendar",
            type: "object",
          },
          {
            field: "optimization_tips",
            as: "optimization_tips",
            type: "object",
          },
          { field: "growth_forecast", as: "growth_forecast", type: "string" },
        ],
      ),
      slackNode(
        "7",
        "Slack: Post weekly channel analysis report",
        1480,
        "📺 *Weekly YouTube Channel Analysis — {channel_name}*\\n\\n*Key Metrics:*\\n• Subscribers: {channel_metrics.subscribers}\\n• Avg Views/Video: {channel_metrics.avg_views_per_video}\\n• Engagement: {channel_metrics.engagement_rate}\\n\\n*Competitive Position:* {engagement_comparison}\\n\\n*Top Recommendations:*\\n1. {strategic_recommendations[0]}\\n2. {strategic_recommendations[1]}\\n3. {strategic_recommendations[2]}\\n\\n*Growth Forecast:* {growth_forecast}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Track performance trends over time",
        1720,
        "YouTube_Analytics",
        '[["{analysis_date}","{channel_metrics.subscribers}","{channel_metrics.total_views}","{channel_metrics.avg_views_per_video}","{channel_metrics.engagement_rate}","{strategic_recommendations[0]}","{growth_forecast}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Comprehensive weekly YouTube channel analysis agent. Fetches channel and competitor statistics from the YouTube Data API, AI-analyzes content performance patterns and competitive positioning, computes key metrics (engagement rate, views per video, growth), generates strategic recommendations with a content calendar, posts the analysis to Slack, and tracks performance trends in Google Sheets.",
  },

  // ─── V3 — Facebook Lead Ad (1) ────────────────────────────────────────

  "Facebook Lead Ad to Email Sequence": {
    nodes: [
      trigger("Webhook: Facebook Lead Ad submission received", "webhook_app", {
        source: "facebook_lead_ads",
        event: "lead_submitted",
        howItStarts:
          "Fires when a new lead submits a Facebook Lead Ad form. Facebook sends the lead data via webhook.",
        setup:
          "Configure Facebook Lead Ads webhook subscription in your Facebook App settings. Point the webhook URL to this workflow's endpoint.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract and normalize lead fields from webhook payload",
        280,
        "You are a lead data processor. Extract and normalize the lead fields from a Facebook Lead Ad webhook payload. Return: email, first_name, last_name, phone (normalized to E.164), company, job_title, custom_answers (array), form_name, campaign_name, ad_name, and submission_timestamp.",
        "Parse and normalize this Facebook Lead Ad webhook payload: {webhook_payload}. Extract all lead fields, normalize phone to E.164 format, and identify the source campaign.",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "phone", as: "lead_phone", type: "string" },
          { field: "company", as: "lead_company", type: "string" },
          { field: "job_title", as: "lead_job_title", type: "string" },
          { field: "campaign_name", as: "lead_campaign", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Clearbit API: Enrich lead with company and firmographic data",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={lead_company_domain}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            ],
            body: [],
            variables: [
              { name: "name", customName: "company_name", type: "string" },
              {
                name: "metrics.employees",
                customName: "company_size",
                type: "number",
              },
              {
                name: "category.industry",
                customName: "company_industry",
                type: "string",
              },
              {
                name: "metrics.annualRevenue",
                customName: "company_revenue",
                type: "number",
              },
              {
                name: "geo.country",
                customName: "company_country",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "HubSpot API: Create or update contact with source attribution",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{lead_email}","firstname":"{lead_first_name}","lastname":"{lead_last_name}","phone":"{lead_phone}","company":"{company_name}","jobtitle":"{lead_job_title}","hs_lead_status":"NEW","lifecyclestage":"lead","num_employees":"{company_size}","industry":"{company_industry}","lead_source":"Facebook Lead Ad","lead_campaign":"{lead_campaign}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.email",
                customName: "hubspot_email",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "5",
        "runCode",
        "Segment lead: enterprise, SMB, product-interest",
        1000,
        {
          runCodeDetails: {
            code: `const size = input.company_size || 0;
const revenue = input.company_revenue || 0;
const industry = input.company_industry || "unknown";
let segment = "smb";
let nurture_sequence = "smb-general";
if (size >= 500 || revenue >= 50000000) {
  segment = "enterprise";
  nurture_sequence = "enterprise-high-touch";
} else if (size >= 50 || revenue >= 5000000) {
  segment = "mid-market";
  nurture_sequence = "mid-market-product-demo";
} else {
  segment = "smb";
  nurture_sequence = "smb-self-serve";
}
const campaign = (input.lead_campaign || "").toLowerCase();
if (campaign.includes("pricing") || campaign.includes("demo")) nurture_sequence = segment + "-product-intent";
else if (campaign.includes("content") || campaign.includes("ebook")) nurture_sequence = segment + "-content-nurture";
return { segment, nurture_sequence, company_size_tier: size >= 500 ? "500+" : size >= 50 ? "50-499" : "1-49", lead_score: segment === "enterprise" ? 85 : segment === "mid-market" ? 60 : 35 };`,
          },
        },
      ),
      emailNode(
        "6",
        "Email: Enroll in appropriate nurture sequence via SendGrid",
        1240,
        "{lead_email}",
        "Welcome, {lead_first_name} — Here's what you requested",
        "Hi {lead_first_name},\\n\\nThank you for your interest! Based on your request through our {lead_campaign} campaign, we've put together some resources tailored for {company_name}.\\n\\nAs a {segment} organization in {company_industry}, here's what we recommend:\\n\\n1. [Resource relevant to your segment]\\n2. [Case study from similar companies]\\n3. [Next step based on nurture sequence: {nurture_sequence}]\\n\\nWe'll be in touch with more personalized resources over the coming days.\\n\\nBest regards,\\nThe Team",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Reconciliation log to verify leads reached CRM",
        1480,
        "Lead_Reconciliation",
        '[["{new Date().toISOString()}","{lead_email}","{lead_first_name} {lead_last_name}","{company_name}","{segment}","{nurture_sequence}","{lead_score}","{hubspot_contact_id}","{lead_campaign}","synced"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Facebook Lead Ad to CRM and nurture pipeline. Captures leads via webhook, AI-extracts and normalizes fields, enriches with Clearbit firmographic data, creates or updates HubSpot contacts with full attribution, segments leads by company size into enterprise/mid-market/SMB with appropriate nurture sequences, sends personalized welcome emails via SendGrid, and logs all leads to a reconciliation sheet.",
  },

  // ─── V4 — Content Production (9) ──────────────────────────────────────

  "Live Commerce Stream Notification": {
    nodes: [
      trigger(
        "Webhook: Live commerce stream scheduled or going live",
        "webhook_app",
        {
          source: "streaming_platform",
          event: "stream_status_change",
          howItStarts:
            "Fires when a live commerce stream is scheduled or transitions to live status on your streaming platform.",
          setup:
            "Configure your streaming platform (Shopify Live, Amazon Live, or custom) to send webhook notifications for stream status changes.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "runCode",
        "Segment audience by product interest and engagement tier",
        280,
        {
          runCodeDetails: {
            code: `const payload = input.webhook_payload || {};
const streamTitle = payload.stream_title || "Live Shopping Event";
const products = payload.featured_products || [];
const scheduledTime = payload.scheduled_time || new Date().toISOString();
const status = payload.status || "scheduled";
const productCategories = products.map(p => p.category || "general");
const segments = {
  high_engagement: { channel: "sms", criteria: "Purchased in last 30 days + opened 3+ emails" },
  medium_engagement: { channel: "email", criteria: "Browsed product categories in last 60 days" },
  general: { channel: "email", criteria: "All subscribers interested in live events" },
};
return { stream_title: streamTitle, products, scheduled_time: scheduledTime, status, product_categories: productCategories, segments, high_engagement_count: 250, medium_engagement_count: 1200, general_count: 5000 };`,
          },
        },
      ),
      emailNode(
        "3",
        "Email: Send 'going live' notification with product preview",
        520,
        "{subscriber_list_email}",
        "We're LIVE — {stream_title} starts now!",
        "Hey there!\\n\\nOur live shopping event is starting RIGHT NOW!\\n\\n🛍️ {stream_title}\\n\\nFeatured Products:\\n{products[0].name} — {products[0].price}\\n{products[1].name} — {products[1].price}\\n\\nExclusive live-only deals and limited inventory. Join now before items sell out!\\n\\n[Watch Live Now →]\\n\\nYou're receiving this because you expressed interest in our live commerce events.",
      ),
      smsNode(
        "4",
        "SMS: Real-time alert to high-engagement segment",
        760,
        "{high_engagement_phone}",
        "🔴 LIVE NOW: {stream_title}! Exclusive deals on {products[0].name} and more. Watch & shop: {stream_url} — Reply STOP to opt out",
      ),
      slackNode(
        "5",
        "Slack: Notify marketing team of stream start",
        1000,
        "🔴 *Live Commerce Stream Started*\\n\\n*{stream_title}*\\n*Status:* {status}\\n*Products:* {products.length} featured\\n*Audience Notified:*\\n• SMS (high-engagement): {high_engagement_count}\\n• Email (medium + general): {medium_engagement_count} + {general_count}\\n\\nMonitor viewer count and engagement in real-time.",
      ),
      n("6", "condition", "Stream ended?", 1240, {
        conditionDetails: {
          conditions: [{ field: "status", operator: "equals", value: "ended" }],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log viewer engagement and conversion metrics",
        1480,
        "Live_Commerce_Metrics",
        '[["{new Date().toISOString()}","{stream_title}","{status}","{products.length}","{high_engagement_count}","{medium_engagement_count}","{general_count}","pending_viewer_data","pending_conversions"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
    summary:
      "Multi-channel live commerce stream notification system. Segments audience by engagement tier (high/medium/general), sends email notifications with product previews to all subscribers, pushes real-time SMS alerts to high-engagement shoppers, notifies the marketing team via Slack, checks stream status, and logs viewer engagement and conversion metrics to Google Sheets.",
  },

  "Podcast Episode Promotion Workflow": {
    nodes: [
      trigger(
        "Webhook: New podcast episode published on Buzzsprout",
        "webhook_app",
        {
          source: "buzzsprout",
          event: "episode.published",
          howItStarts:
            "Fires when a new podcast episode is published on Buzzsprout, delivering episode metadata via webhook.",
          setup:
            "Configure Buzzsprout webhook to send episode.published events to this workflow's endpoint URL.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Generate multi-format promotion copy — social, email, audiogram",
        280,
        "You are a podcast marketing expert. Generate promotion assets for a new episode. Return: twitter_posts (array of 3 tweets with hashtags), linkedin_post (professional angle), instagram_caption (with emoji and hashtags), email_newsletter_block (subject line + body paragraph), audiogram_script (30-second highlight quote), key_quotes (array of 3 shareable quotes), and episode_summary (2 sentences).",
        "New podcast episode published: Title: {episode_title}. Description: {episode_description}. Guest: {guest_name}. Duration: {episode_duration}. Topics: {episode_topics}. Generate all promotion assets.",
        [
          { field: "twitter_posts", as: "twitter_posts", type: "object" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "email_newsletter_block",
            as: "newsletter_block",
            type: "object",
          },
          { field: "audiogram_script", as: "audiogram_script", type: "string" },
          { field: "key_quotes", as: "key_quotes", type: "object" },
          { field: "episode_summary", as: "episode_summary", type: "string" },
        ],
      ),
      n("3", "runCode", "Create promotion task checklist per channel", 520, {
        runCodeDetails: {
          code: `const now = new Date();
const tasks = [
  { task: "Post Twitter thread (3 tweets)", channel: "Twitter/X", due: now.toISOString().split("T")[0], status: "ready", content_ready: true },
  { task: "Post LinkedIn article", channel: "LinkedIn", due: now.toISOString().split("T")[0], status: "ready", content_ready: true },
  { task: "Post Instagram with audiogram", channel: "Instagram", due: new Date(now.getTime() + 86400000).toISOString().split("T")[0], status: "pending_audiogram", content_ready: false },
  { task: "Send email newsletter", channel: "Email", due: new Date(now.getTime() + 86400000).toISOString().split("T")[0], status: "ready", content_ready: true },
  { task: "Create YouTube Shorts clip", channel: "YouTube", due: new Date(now.getTime() + 2 * 86400000).toISOString().split("T")[0], status: "pending_clip", content_ready: false },
  { task: "Submit to podcast directories", channel: "Directories", due: now.toISOString().split("T")[0], status: "auto", content_ready: true },
];
return { promotion_tasks: tasks, total_tasks: tasks.length, ready_count: tasks.filter(t => t.content_ready).length, episode_title: input.episode_title };`,
        },
      }),
      emailNode(
        "4",
        "Email: Send guest kit with episode links and social assets",
        760,
        "{guest_email}",
        "Your episode is live! Here's your guest sharing kit — {episode_title}",
        "Hi {guest_name},\\n\\nGreat news — our episode is now live!\\n\\n🎙️ {episode_title}\\n\\nHere's your sharing kit:\\n\\n📱 Social Posts (ready to copy-paste):\\n• Twitter: {twitter_posts[0]}\\n• LinkedIn: {linkedin_post}\\n\\n🔗 Episode Links:\\n• Apple Podcasts: {apple_url}\\n• Spotify: {spotify_url}\\n• Web Player: {web_url}\\n\\n💬 Key Quotes to Share:\\n• {key_quotes[0]}\\n• {key_quotes[1]}\\n\\nThank you for being an amazing guest! Please share with your network — it makes a huge difference.\\n\\nBest,\\n{host_name}",
      ),
      slackNode(
        "5",
        "Slack: Distribute promotion assets to social media team",
        1000,
        "🎙️ *New Episode Published — Promotion Assets Ready*\\n\\n*{episode_title}*\\nGuest: {guest_name}\\n\\n*Ready Tasks:* {ready_count}/{total_tasks}\\n\\n*Twitter (3 posts ready):*\\n{twitter_posts[0]}\\n\\n*LinkedIn:*\\n{linkedin_post}\\n\\n*Audiogram Script:*\\n{audiogram_script}\\n\\n*Key Quotes:*\\n• {key_quotes[0]}\\n• {key_quotes[1]}\\n• {key_quotes[2]}",
      ),
      n("6", "condition", "Guest provided for this episode?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "guest_name", operator: "is_not_empty", value: "" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Send guest thank-you with sharing resources",
        1480,
        "{guest_email}",
        "Thank you for joining us, {guest_name}!",
        "Hi {guest_name},\\n\\nThank you again for being a guest on our podcast!\\n\\nThe episode '{episode_title}' has been performing well and your audience would love it too.\\n\\nIf you haven't already shared it, here are some quick links:\\n• {web_url}\\n• {spotify_url}\\n\\nWe'd also love to have you back for a follow-up episode. Let us know if you're interested!\\n\\nWarmly,\\n{host_name}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Track promotion reach and downloads",
        1720,
        "Podcast_Promotion",
        '[["{new Date().toISOString()}","{episode_title}","{guest_name}","{total_tasks}","{ready_count}","{episode_summary}","pending_download_count","pending_social_reach"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Comprehensive podcast episode promotion workflow. Generates multi-format promotion copy (Twitter threads, LinkedIn posts, Instagram captions, audiogram scripts, newsletter blocks) from episode metadata, creates a per-channel promotion task checklist, sends a guest sharing kit with social assets and episode links, distributes promotion assets to the social team via Slack, sends guest thank-you follow-ups, and tracks promotion reach in Google Sheets.",
  },

  "Podcast Guest Booking Bot": {
    nodes: [
      trigger(
        "Webhook: Potential guest submission or lead received",
        "webhook_app",
        {
          source: "outgrow-forms",
          event: "form_response",
          howItStarts:
            "Fires when a potential podcast guest submits an application form or is added as a lead via Outgrow Forms or similar intake form.",
          setup:
            "Create a guest application form on Outgrow Forms and configure the webhook to send responses to this workflow endpoint.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Research guest background and score fit for podcast topics",
        280,
        "You are a podcast booking researcher. Analyze the potential guest submission and score their fit. Return: guest_name, expertise_areas (array), audience_reach_estimate (number), topic_relevance_score (0-100), previous_podcast_appearances (estimated count), social_following (estimated), unique_angle (what makes them interesting), and fit_summary (2 sentences).",
        "Potential guest submission: {webhook_payload}. Our podcast covers: {podcast_topics}. Target audience: {target_listener_profile}. Research this guest and score their fit.",
        [
          { field: "guest_name", as: "guest_name", type: "string" },
          { field: "expertise_areas", as: "expertise_areas", type: "object" },
          {
            field: "audience_reach_estimate",
            as: "audience_reach",
            type: "number",
          },
          {
            field: "topic_relevance_score",
            as: "relevance_score",
            type: "number",
          },
          { field: "unique_angle", as: "unique_angle", type: "string" },
          { field: "fit_summary", as: "fit_summary", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Rank guest candidates by expertise, audience reach, and topic relevance",
        520,
        {
          runCodeDetails: {
            code: `const relevance = input.relevance_score || 0;
const reach = input.audience_reach || 0;
const expertise = (input.expertise_areas || []).length;
const reachScore = reach >= 100000 ? 30 : reach >= 10000 ? 20 : reach >= 1000 ? 10 : 5;
const expertiseScore = expertise >= 5 ? 20 : expertise >= 3 ? 15 : expertise >= 1 ? 10 : 5;
const compositeScore = relevance * 0.5 + reachScore + expertiseScore;
const tier = compositeScore >= 75 ? "A-tier" : compositeScore >= 50 ? "B-tier" : "C-tier";
return { composite_score: Math.round(compositeScore), tier, relevance_score: relevance, reach_score: reachScore, expertise_score: expertiseScore, guest_name: input.guest_name, passes_threshold: compositeScore >= 75, booking_priority: compositeScore >= 75 ? "high" : compositeScore >= 50 ? "medium" : "low" };`,
          },
        },
      ),
      n("4", "condition", "Fit score above 75 threshold?", 760, {
        conditionDetails: {
          conditions: [
            { field: "passes_threshold", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send personalized booking pitch to guest",
        1000,
        "{guest_email}",
        "Invitation to join {podcast_name} — {guest_name}",
        "Hi {guest_name},\\n\\nI'm the host of {podcast_name}, and I've been following your work in {expertise_areas[0]}.\\n\\n{unique_angle} — and I think our audience of {target_listener_profile} would love to hear your perspective.\\n\\nI'd love to have you on for a {episode_length}-minute conversation about {suggested_topic}.\\n\\nHere's what to expect:\\n• Pre-recorded, edited for quality\\n• Shared with our {subscriber_count} subscribers\\n• Full promotion across our channels\\n\\nWould you be interested? If so, you can pick a time that works:\\n{calendly_link}\\n\\nLooking forward to hearing from you!\\n\\nBest,\\n{host_name}",
      ),
      n(
        "6",
        "hitApi",
        "Calendly API: Share scheduling link for recording",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.calendly.com/scheduling_links",
            headers: [
              { key: "Authorization", value: "Bearer {calendly_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "max_event_count", value: "1" },
              { key: "owner", value: "{calendly_event_type_uri}" },
              { key: "owner_type", value: "EventType" },
            ],
            variables: [
              {
                name: "resource.booking_url",
                customName: "booking_url",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Notify host of booked guest with research brief",
        1480,
        "🎙️ *New Guest Booking — {tier}*\\n\\n*Guest:* {guest_name}\\n*Composite Score:* {composite_score}/100\\n*Relevance:* {relevance_score} | *Reach:* {reach_score} | *Expertise:* {expertise_score}\\n\\n*Fit Summary:* {fit_summary}\\n*Unique Angle:* {unique_angle}\\n*Expertise:* {expertise_areas}\\n\\n*Booking Link:* {booking_url}\\n*Priority:* {booking_priority}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Track guest pipeline and booking rate",
        1720,
        "Guest_Pipeline",
        '[["{new Date().toISOString()}","{guest_name}","{composite_score}","{tier}","{booking_priority}","{fit_summary}","{unique_angle}","pitch_sent","{booking_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automated podcast guest booking pipeline. Receives guest applications via webhook, AI-researches background and scores fit against podcast topics, ranks candidates by composite score (expertise, audience reach, relevance), sends personalized booking pitches to qualified guests, generates Calendly scheduling links, notifies the host via Slack with a full research brief, and tracks the guest pipeline and booking rates in Google Sheets.",
  },

  "Podcast Guest Outreach": {
    nodes: [
      trigger("Manual trigger: Host adds guest prospect", "manual", {
        howItStarts:
          "The podcast host manually adds a guest prospect with their name, email, bio, and suggested topic.",
        setup:
          "Trigger this workflow when you want to initiate outreach to a potential podcast guest.",
        inputFields: [
          { name: "guest_name", type: "string", example: "Sarah Chen" },
          { name: "guest_email", type: "string", example: "sarah@example.com" },
          {
            name: "guest_bio",
            type: "string",
            example:
              "VP of Product at Stripe, previously founded two B2B SaaS startups",
          },
          {
            name: "suggested_topic",
            type: "string",
            example: "Building product-led growth at scale",
          },
          {
            name: "podcast_name",
            type: "string",
            example: "The Growth Playbook",
          },
        ],
      }),
      aiNode(
        "2",
        "AI: Research prospect and generate personalized pitch",
        280,
        "You are a podcast outreach specialist. Research the prospect and generate a highly personalized outreach email pitch. Reference specific work, achievements, or content they've published. Return: personalized_hook (specific reference to their work), value_proposition (why their audience benefits), suggested_topics (array of 3 specific episode angles), social_proof (mention similar guests or audience size), and full_pitch_email (complete email body).",
        "Guest: {guest_name}. Bio: {guest_bio}. Suggested topic: {suggested_topic}. Podcast: {podcast_name}. Generate a personalized, research-backed outreach pitch.",
        [
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
          { field: "suggested_topics", as: "suggested_topics", type: "object" },
          { field: "full_pitch_email", as: "pitch_email_body", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email: Send outreach with podcast value proposition",
        520,
        "{guest_email}",
        "Would love to have you on {podcast_name}, {guest_name}",
        "{pitch_email_body}",
      ),
      n("4", "delayAction", "Wait 5 days for response", 760, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("5", "condition", "Response received from guest?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "guest_responded", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send follow-up with social proof",
        1240,
        "{guest_email}",
        "Quick follow-up — {podcast_name} interview, {guest_name}",
        "Hi {guest_name},\\n\\nI wanted to follow up on my earlier note about joining {podcast_name}.\\n\\nSince I last reached out, we've had some great conversations with leaders like [recent notable guest] about similar topics. Our episode on {suggested_topics[0]} has been one of our most popular this quarter.\\n\\nI think your perspective on {suggested_topic} would resonate strongly with our audience of [audience description].\\n\\nWould a 30-minute recorded conversation work for you? Happy to work around your schedule.\\n\\nBest,\\n{host_name}\\n\\nP.S. Here's a recent episode so you can get a feel for the format: [episode_link]",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Track outreach pipeline and response rates",
        1480,
        "Guest_Outreach",
        '[["{new Date().toISOString()}","{guest_name}","{guest_email}","{suggested_topic}","{personalized_hook}","outreach_sent","pending_response"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
    summary:
      "Podcast guest outreach workflow with intelligent follow-up. AI-researches the prospect and generates a highly personalized pitch referencing their specific work, sends the initial outreach email, waits 5 days, checks for a response, sends a social-proof follow-up to non-responders, and tracks the entire outreach pipeline and response rates in Google Sheets.",
  },

  "Podcast Listening Queue Manager": {
    nodes: [
      trigger("Webhook: New episode added to listening queue", "webhook_app", {
        source: "pocket_casts",
        event: "episode_queued",
        howItStarts:
          "Fires when a new podcast episode is added to the listening queue from any podcast app or manual submission.",
        setup:
          "Configure your podcast app or RSS monitor to send webhooks when new episodes are queued.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Deduplicate and organize queue by priority and topic",
        280,
        {
          runCodeDetails: {
            code: `const payload = input.webhook_payload || {};
const episodeTitle = payload.episode_title || "Unknown Episode";
const podcastName = payload.podcast_name || "Unknown Podcast";
const duration = payload.duration_minutes || 30;
const publishDate = payload.published_date || new Date().toISOString();
const topics = payload.topics || ["general"];
const existingQueue = input.current_queue || [];
const isDuplicate = existingQueue.some(e => e.episode_title === episodeTitle && e.podcast_name === podcastName);
if (isDuplicate) return { is_duplicate: true, queue_length: existingQueue.length, episode_title: episodeTitle };
const priorityScore = (topics.includes("business") || topics.includes("tech") ? 3 : 1) + (duration <= 30 ? 2 : duration <= 60 ? 1 : 0);
const entry = { episode_title: episodeTitle, podcast_name: podcastName, duration, publish_date: publishDate, topics, priority: priorityScore >= 4 ? "high" : priorityScore >= 2 ? "medium" : "low", added_at: new Date().toISOString() };
const updatedQueue = [...existingQueue, entry].sort((a, b) => (b.priority === "high" ? 3 : b.priority === "medium" ? 2 : 1) - (a.priority === "high" ? 3 : a.priority === "medium" ? 2 : 1));
return { is_duplicate: false, queue_length: updatedQueue.length, episode_title: episodeTitle, podcast_name: podcastName, priority: entry.priority, queue_over_limit: updatedQueue.length > 20, updated_queue: updatedQueue };`,
          },
        },
      ),
      aiNode(
        "3",
        "AI: Generate episode summary from description",
        520,
        "You are a podcast content summarizer. Generate a concise summary from the episode description that helps the listener decide whether to listen. Return: summary (2-3 sentences), key_topics (array of tags), estimated_value (high/medium/low based on topic relevance), and listen_recommendation (brief reason to listen or skip).",
        "Episode: {episode_title}. Podcast: {podcast_name}. Description: {episode_description}. Duration: {duration} minutes. Generate a concise summary to help decide whether to listen.",
        [
          { field: "summary", as: "episode_summary", type: "string" },
          { field: "key_topics", as: "key_topics", type: "object" },
          { field: "estimated_value", as: "estimated_value", type: "string" },
          {
            field: "listen_recommendation",
            as: "listen_recommendation",
            type: "string",
          },
        ],
      ),
      slackNode(
        "4",
        "Slack: Send daily queue digest with summaries",
        760,
        "🎧 *Podcast Queue Update*\\n\\n*Added:* {episode_title} ({podcast_name})\\n*Priority:* {priority}\\n*Duration:* {duration} min\\n\\n*Summary:* {episode_summary}\\n*Recommendation:* {listen_recommendation}\\n\\n*Queue Length:* {queue_length} episodes",
      ),
      n("5", "condition", "Queue exceeds 20 episodes?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "queue_over_limit", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Suggest pruning low-priority items",
        1240,
        "⚠️ *Podcast Queue Overflow — {queue_length} Episodes*\\n\\nYour queue has exceeded 20 episodes. Consider pruning low-priority items:\\n\\n*Suggested for removal (oldest low-priority):*\\n• Check your queue sheet for items marked 'low' priority\\n• Episodes older than 30 days may be less relevant\\n\\nTake 5 minutes to clean up and keep your queue focused.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Maintain queue state",
        1480,
        "Podcast_Queue",
        '[["{new Date().toISOString()}","{episode_title}","{podcast_name}","{duration}","{priority}","{episode_summary}","{estimated_value}","{listen_recommendation}","queued"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
    summary:
      "Podcast listening queue management system. Deduplicates incoming episodes, organizes by priority and topic, generates AI summaries with listen/skip recommendations, sends queue digests via Slack, warns when the queue exceeds 20 episodes with pruning suggestions, and maintains the full queue state in Google Sheets.",
  },

  "TikTok Trend Spotter": {
    nodes: [
      trigger("Schedule: Daily at 8 AM — scan TikTok trends", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to fetch trending sounds, hashtags, and content patterns from TikTok and identify brand-relevant trends.",
        setup:
          "Configure your TikTok for Business API credentials and set your brand relevance keywords in the AI analysis node.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "TikTok API: Fetch trending sounds, hashtags, and content patterns",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://business-api.tiktok.com/open_api/v1.3/creative/trending/?advertiser_id={tiktok_advertiser_id}&country_code=US&period=7",
            headers: [{ key: "Access-Token", value: "{tiktok_access_token}" }],
            body: [],
            variables: [
              {
                name: "data.trending_sounds",
                customName: "trending_sounds",
                type: "object",
              },
              {
                name: "data.trending_hashtags",
                customName: "trending_hashtags",
                type: "object",
              },
              {
                name: "data.trending_creators",
                customName: "trending_creators",
                type: "object",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze trends for brand relevance and filter spam",
        520,
        "You are a TikTok trend analyst for brands. Analyze the trending sounds, hashtags, and content patterns. Filter out trend spam, inappropriate content, and irrelevant viral moments. For each relevant trend, provide: trend_name, trend_type (sound/hashtag/format), relevance_score (0-100), brand_safety (safe/caution/unsafe), viral_potential (high/medium/low), content_angle (how a brand could use this), and expiration_estimate (days until trend fades). Return only trends with relevance_score > 40.",
        "Brand keywords: {brand_keywords}. Industry: {brand_industry}. Target audience: {target_audience}. Trending sounds: {trending_sounds}. Trending hashtags: {trending_hashtags}. Analyze for brand relevance and filter spam.",
        [
          { field: "relevant_trends", as: "relevant_trends", type: "object" },
          { field: "trend_summary", as: "trend_summary", type: "string" },
          { field: "top_opportunity", as: "top_opportunity", type: "object" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Score trends by relevance, virality potential, and brand safety",
        760,
        {
          runCodeDetails: {
            code: `const trends = input.relevant_trends || [];
const scored = trends.map(t => {
  const relevance = t.relevance_score || 0;
  const virality = t.viral_potential === "high" ? 30 : t.viral_potential === "medium" ? 20 : 10;
  const safety = t.brand_safety === "safe" ? 20 : t.brand_safety === "caution" ? 10 : 0;
  const composite = relevance * 0.5 + virality + safety;
  return { ...t, composite_score: Math.round(composite), actionable: composite >= 60 };
}).sort((a, b) => b.composite_score - a.composite_score);
const highRelevance = scored.filter(t => t.actionable);
return { scored_trends: scored, high_relevance_trends: highRelevance, high_relevance_count: highRelevance.length, any_high_relevance: highRelevance.length > 0, total_trends_analyzed: trends.length, scan_date: new Date().toISOString().split("T")[0] };`,
          },
        },
      ),
      n("5", "condition", "Any high-relevance trends found?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "any_high_relevance", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Alert content team with trend briefs and suggested angles",
        1240,
        "🔥 *TikTok Trend Alert — {high_relevance_count} Actionable Trends*\\n\\n{trend_summary}\\n\\n*Top Opportunity:*\\n• Trend: {top_opportunity.trend_name}\\n• Type: {top_opportunity.trend_type}\\n• Score: {top_opportunity.composite_score}/100\\n• Brand Safety: {top_opportunity.brand_safety}\\n• Content Angle: {top_opportunity.content_angle}\\n• Estimated Lifespan: {top_opportunity.expiration_estimate} days\\n\\n*Act fast — trends expire quickly!*",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Archive trend data for analysis",
        1480,
        "TikTok_Trends",
        '[["{scan_date}","{high_relevance_count}","{total_trends_analyzed}","{top_opportunity.trend_name}","{top_opportunity.trend_type}","{top_opportunity.composite_score}","{top_opportunity.content_angle}","{trend_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
    summary:
      "Daily TikTok trend detection and alerting system. Fetches trending sounds, hashtags, and content patterns from the TikTok API, AI-analyzes for brand relevance while filtering spam and unsafe content, scores trends by composite relevance/virality/safety metrics, alerts the content team via Slack with actionable trend briefs and suggested content angles, and archives all trend data to Google Sheets for longitudinal analysis.",
  },

  "Video Transcript to Blog Converter": {
    nodes: [
      trigger("Webhook: New video transcript uploaded", "webhook_app", {
        source: "cloud_storage",
        event: "file.created",
        howItStarts:
          "Fires when a new video transcript file is uploaded to cloud storage (Google Drive, Dropbox, or S3).",
        setup:
          "Configure your cloud storage to send webhooks when new transcript files are added to the designated folder.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Clean and structure raw transcript",
        280,
        "You are a transcript editor. Clean the raw video transcript: remove filler words (um, uh, like, you know), fix grammar, identify speakers, add punctuation, and break into logical sections. Return: cleaned_transcript, sections (array with title and content), speaker_names (array), word_count, and estimated_read_time.",
        "Clean and structure this raw video transcript: {raw_transcript}",
        [
          {
            field: "cleaned_transcript",
            as: "cleaned_transcript",
            type: "string",
          },
          { field: "sections", as: "transcript_sections", type: "object" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "estimated_read_time", as: "read_time", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI: Convert transcript into SEO-optimized blog post",
        520,
        "You are an SEO content writer. Convert the cleaned transcript into a full blog post. Include: title (SEO-optimized, 50-60 chars), meta_description (150-160 chars), blog_body (full HTML with h2/h3 headings, bullet points, blockquotes for key quotes), key_takeaways (array of 5), primary_keyword, secondary_keywords (array), internal_link_suggestions (array of 3 anchor text + topic pairs), and cta_section.",
        "Transcript sections: {transcript_sections}. Word count: {word_count}. Video title: {video_title}. Target audience: {target_audience}. Convert into an SEO-optimized blog post.",
        [
          { field: "title", as: "blog_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "blog_body", as: "blog_body", type: "string" },
          { field: "primary_keyword", as: "primary_keyword", type: "string" },
          {
            field: "secondary_keywords",
            as: "secondary_keywords",
            type: "object",
          },
          { field: "key_takeaways", as: "key_takeaways", type: "object" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Validate SEO elements: meta description, keyword density, internal links",
        760,
        {
          runCodeDetails: {
            code: `const title = input.blog_title || "";
const meta = input.meta_description || "";
const body = input.blog_body || "";
const keyword = input.primary_keyword || "";
const issues = [];
if (title.length < 50 || title.length > 60) issues.push("Title length " + title.length + " chars (target: 50-60)");
if (meta.length < 150 || meta.length > 160) issues.push("Meta description " + meta.length + " chars (target: 150-160)");
const wordCount = body.split(/\\s+/).length;
const keywordCount = (body.toLowerCase().match(new RegExp(keyword.toLowerCase(), "g")) || []).length;
const density = wordCount > 0 ? ((keywordCount / wordCount) * 100).toFixed(2) : 0;
if (parseFloat(density) < 0.5) issues.push("Keyword density " + density + "% is too low (target: 0.5-2.5%)");
if (parseFloat(density) > 2.5) issues.push("Keyword density " + density + "% is too high (target: 0.5-2.5%)");
const h2Count = (body.match(/<h2/g) || []).length;
if (h2Count < 3) issues.push("Only " + h2Count + " H2 headings (recommend 3+)");
return { seo_score: Math.max(0, 100 - issues.length * 15), issues, issue_count: issues.length, title_length: title.length, meta_length: meta.length, keyword_density: density + "%", word_count: wordCount, is_valid: issues.length <= 1 };`,
          },
        },
      ),
      n("5", "approvalGate", "Editorial lead reviews blog draft", 1000, {
        approvalDetails: {
          approver: "editorial-lead",
          timeout: 72,
          timeoutUnit: "hours",
        },
      }),
      n("6", "hitApi", "WordPress API: Publish blog post to CMS", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wordpress_site}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Bearer {wordpress_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{blog_title}" },
            { key: "content", value: "{blog_body}" },
            { key: "excerpt", value: "{meta_description}" },
            { key: "status", value: "publish" },
            { key: "categories", value: "[{blog_category_id}]" },
            {
              key: "meta",
              value:
                '{"_yoast_wpseo_metadesc":"{meta_description}","_yoast_wpseo_focuskw":"{primary_keyword}"}',
            },
          ],
          variables: [
            { name: "id", customName: "post_id", type: "string" },
            { name: "link", customName: "post_url", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify content team of new blog post",
        1480,
        "📝 *New Blog Post Published*\\n\\n*Title:* {blog_title}\\n*URL:* {post_url}\\n*Keyword:* {primary_keyword}\\n*SEO Score:* {seo_score}/100\\n*Word Count:* {word_count}\\n*Source:* Video transcript conversion\\n\\n*Key Takeaways:*\\n• {key_takeaways[0]}\\n• {key_takeaways[1]}\\n• {key_takeaways[2]}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Track blog performance metrics",
        1720,
        "Blog_Performance",
        '[["{new Date().toISOString()}","{blog_title}","{post_url}","{primary_keyword}","{seo_score}","{word_count}","{keyword_density}","video_transcript","pending_views","pending_engagement"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Converts video transcripts into SEO-optimized blog posts. Cleans and structures raw transcripts, generates full blog posts with headings, key takeaways, and CTAs, validates SEO elements (title length, meta description, keyword density), routes through editorial approval, publishes to WordPress via API, notifies the content team via Slack, and tracks blog performance metrics in Google Sheets.",
  },

  "YouTube Video Pipeline": {
    nodes: [
      trigger("Manual trigger: Creator submits video topic", "manual", {
        howItStarts:
          "A creator or content manager submits a new video topic with target audience, desired length, and content goals.",
        setup:
          "Trigger this workflow when a new YouTube video is ready to enter the production pipeline.",
        inputFields: [
          {
            name: "video_topic",
            type: "string",
            example: "How to Build a $10K/mo SaaS in 90 Days",
          },
          {
            name: "target_audience",
            type: "string",
            example:
              "Aspiring SaaS founders, indie hackers, technical entrepreneurs",
          },
          { name: "video_length_minutes", type: "number", example: "15" },
          {
            name: "content_goal",
            type: "string",
            example: "Drive newsletter signups and establish authority",
          },
          { name: "channel_name", type: "string", example: "The SaaS Builder" },
        ],
      }),
      aiNode(
        "2",
        "AI: Generate video script outline with hook, body, CTA",
        280,
        "You are a YouTube scriptwriter specializing in high-retention content. Generate a complete video script outline. Include: hook (first 15 seconds to capture attention), intro (brief channel intro and topic preview), main_sections (array of 3-5 sections with title, key_points, visual_cues, and estimated_duration), cta_section (specific call-to-action with end screen strategy), outro, and retention_tips (array of techniques to keep viewers engaged).",
        "Topic: {video_topic}. Audience: {target_audience}. Length: {video_length_minutes} minutes. Goal: {content_goal}. Generate a high-retention video script outline.",
        [
          { field: "hook", as: "video_hook", type: "string" },
          { field: "main_sections", as: "script_sections", type: "object" },
          { field: "cta_section", as: "cta_section", type: "string" },
          { field: "retention_tips", as: "retention_tips", type: "object" },
          {
            field: "estimated_duration",
            as: "estimated_duration",
            type: "string",
          },
        ],
      ),
      n("3", "approvalGate", "Host/creator approves script outline", 520, {
        approvalDetails: {
          approver: "host-creator",
          timeout: 48,
          timeoutUnit: "hours",
        },
      }),
      n(
        "4",
        "runCode",
        "Create production task checklist: filming, editing, thumbnails",
        760,
        {
          runCodeDetails: {
            code: `const now = new Date();
const sections = input.script_sections || [];
const tasks = [
  { task: "Review and finalize script", assignee: "host", due: new Date(now.getTime() + 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Set up filming equipment and lighting", assignee: "videographer", due: new Date(now.getTime() + 2 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Film " + sections.length + " main sections + intro/outro", assignee: "host", due: new Date(now.getTime() + 3 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Record B-roll and screen captures", assignee: "videographer", due: new Date(now.getTime() + 3 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Edit video with transitions and graphics", assignee: "editor", due: new Date(now.getTime() + 5 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Add captions and subtitles", assignee: "editor", due: new Date(now.getTime() + 6 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Create 3 thumbnail options", assignee: "designer", due: new Date(now.getTime() + 5 * 86400000).toISOString().split("T")[0], status: "pending" },
  { task: "Final review and approval", assignee: "host", due: new Date(now.getTime() + 7 * 86400000).toISOString().split("T")[0], status: "pending" },
];
return { production_tasks: tasks, total_tasks: tasks.length, estimated_publish_date: new Date(now.getTime() + 8 * 86400000).toISOString().split("T")[0] };`,
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Assign production tasks to team",
        1000,
        "🎬 *YouTube Video Pipeline — Tasks Assigned*\\n\\n*Topic:* {video_topic}\\n*Sections:* {script_sections.length} main sections\\n*Estimated Duration:* {estimated_duration}\\n*Target Publish:* {estimated_publish_date}\\n\\n*Tasks ({total_tasks}):*\\n• Script finalization → Host\\n• Filming setup → Videographer\\n• Recording → Host + Videographer\\n• Editing → Editor\\n• Thumbnails → Designer\\n• Final review → Host\\n\\n*Hook:* {video_hook}",
      ),
      aiNode(
        "6",
        "AI: Generate YouTube metadata — title, description, tags, thumbnail brief",
        1240,
        "You are a YouTube SEO expert. Generate optimized metadata for the video. Return: title (max 70 chars, attention-grabbing with keyword), description (2000+ chars with timestamps, links, keywords), tags (array of 15-20 relevant tags), thumbnail_brief (specific visual direction: text overlay, colors, facial expression, composition), and category (YouTube category ID).",
        "Topic: {video_topic}. Audience: {target_audience}. Script sections: {script_sections}. Goal: {content_goal}. Channel: {channel_name}. Generate optimized YouTube metadata.",
        [
          { field: "title", as: "youtube_title", type: "string" },
          { field: "description", as: "youtube_description", type: "string" },
          { field: "tags", as: "youtube_tags", type: "object" },
          { field: "thumbnail_brief", as: "thumbnail_brief", type: "string" },
          { field: "category", as: "youtube_category", type: "string" },
        ],
      ),
      n("7", "hitApi", "YouTube Upload API: Configure video settings", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/youtube/v3/videos?part=snippet,status",
          headers: [
            { key: "Authorization", value: "Bearer {google_oauth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "snippet",
              value:
                '{"title":"{youtube_title}","description":"{youtube_description}","tags":{youtube_tags},"categoryId":"{youtube_category}"}',
            },
            {
              key: "status",
              value:
                '{"privacyStatus":"private","selfDeclaredMadeForKids":false,"publishAt":"{estimated_publish_date}T12:00:00Z"}',
            },
          ],
          variables: [
            { name: "id", customName: "youtube_video_id", type: "string" },
            {
              name: "snippet.title",
              customName: "published_title",
              type: "string",
            },
          ],
        },
      }),
      n("8", "condition", "Video published successfully?", 1720, {
        conditionDetails: {
          conditions: [
            { field: "youtube_video_id", operator: "is_not_empty", value: "" },
          ],
        },
      }),
      sheetsNode(
        "9",
        "Google Sheets: Track video pipeline and performance",
        1960,
        "YouTube_Pipeline",
        '[["{new Date().toISOString()}","{video_topic}","{youtube_title}","{youtube_video_id}","{total_tasks}","{estimated_publish_date}","{youtube_category}","{thumbnail_brief}","pipeline_active","pending_views"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "9", "output_2"),
    ],
    summary:
      "Full YouTube video production pipeline from topic to publish. Generates a high-retention script outline with hooks and CTAs, routes through creator approval, creates a detailed production task checklist, assigns tasks to the team via Slack, generates SEO-optimized YouTube metadata (title, description, tags, thumbnail brief), configures video settings via the YouTube API, and tracks the entire pipeline in Google Sheets.",
  },

  "YouTube Video Summarizer to Newsletter": {
    nodes: [
      trigger("Webhook: New YouTube video published", "webhook_app", {
        source: "youtube",
        event: "video.published",
        howItStarts:
          "Fires when a new YouTube video is published on the channel, sending the video metadata via webhook.",
        setup:
          "Configure YouTube channel webhook or use YouTube Data API push notifications to trigger this workflow on new video publications.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "YouTube API: Fetch video transcript via captions",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/youtube/v3/captions?part=snippet&videoId={youtube_video_id}&key={youtube_api_key}",
            headers: [
              { key: "Authorization", value: "Bearer {google_oauth_token}" },
            ],
            body: [],
            variables: [
              {
                name: "items[0].id",
                customName: "caption_track_id",
                type: "string",
              },
              {
                name: "items[0].snippet.language",
                customName: "caption_language",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Summarize video into newsletter format with key takeaways and timestamps",
        520,
        "You are a newsletter content creator. Summarize the video transcript into a compelling newsletter format. Include: subject_line (attention-grabbing, 50 chars max), preview_text (90 chars), intro_paragraph (hook readers), key_takeaways (array of 5 with timestamps), notable_quotes (array of 2-3 memorable quotes), action_items (what readers should do), video_link_cta (compelling reason to watch the full video), and estimated_read_time.",
        "Video title: {video_title}. Video transcript: {video_transcript}. Duration: {video_duration}. Channel: {channel_name}. Create a newsletter summary.",
        [
          { field: "subject_line", as: "newsletter_subject", type: "string" },
          { field: "preview_text", as: "preview_text", type: "string" },
          { field: "intro_paragraph", as: "intro_paragraph", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "object" },
          { field: "notable_quotes", as: "notable_quotes", type: "object" },
          { field: "action_items", as: "action_items", type: "object" },
          { field: "video_link_cta", as: "video_cta", type: "string" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Format newsletter blocks with trackable links",
        1000 - 240,
        {
          runCodeDetails: {
            code: `const subject = input.newsletter_subject || "";
const intro = input.intro_paragraph || "";
const takeaways = input.key_takeaways || [];
const quotes = input.notable_quotes || [];
const actions = input.action_items || [];
const videoCta = input.video_cta || "";
const videoUrl = "https://youtube.com/watch?v=" + (input.youtube_video_id || "");
const trackableVideoUrl = videoUrl + "?utm_source=newsletter&utm_medium=email&utm_campaign=" + encodeURIComponent(subject);
const takeawayHtml = takeaways.map((t, i) => "<li><strong>" + t.timestamp + "</strong> — " + t.takeaway + "</li>").join("");
const quoteHtml = quotes.map(q => "<blockquote>" + q + "</blockquote>").join("");
const actionHtml = actions.map(a => "<li>" + a + "</li>").join("");
const newsletterHtml = "<h2>" + (input.video_title || "") + "</h2>" + "<p>" + intro + "</p>" + "<h3>Key Takeaways</h3><ol>" + takeawayHtml + "</ol>" + quoteHtml + "<h3>Action Items</h3><ul>" + actionHtml + "</ul>" + "<p><a href=\\"" + trackableVideoUrl + "\\">" + videoCta + "</a></p>";
return { newsletter_html: newsletterHtml, trackable_video_url: trackableVideoUrl, block_count: 4, subject: subject };`,
          },
        },
      ),
      n("5", "approvalGate", "Editor reviews newsletter content", 1000, {
        approvalDetails: {
          approver: "newsletter-editor",
          timeout: 24,
          timeoutUnit: "hours",
        },
      }),
      emailNode(
        "6",
        "Email: Send newsletter via SendGrid to subscriber list",
        1240,
        "{subscriber_list_email}",
        "{newsletter_subject}",
        "{newsletter_html}",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Track newsletter open rates and click-through",
        1480,
        "Newsletter_Tracking",
        '[["{new Date().toISOString()}","{video_title}","{newsletter_subject}","{youtube_video_id}","{trackable_video_url}","sent","pending_opens","pending_clicks","pending_ctr"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Converts new YouTube videos into newsletter editions. Fetches video transcripts via the YouTube captions API, AI-summarizes into newsletter format with key takeaways and timestamps, formats HTML newsletter blocks with UTM-tracked links, routes through editor approval, sends the newsletter via email to the subscriber list, and tracks open rates and click-through metrics in Google Sheets.",
  },

  // ─── Webhook Connectors (9) ───────────────────────────────────────────

  "Airtable Record to Trello Card": {
    nodes: [
      trigger("Webhook: Airtable record created or updated", "webhook_app", {
        source: "airtable",
        event: "record.created_or_updated",
        howItStarts:
          "Fires when a record is created or updated in your Airtable base, sending the record data via webhook.",
        setup:
          "Configure Airtable automation to send a webhook when records in your target table are created or modified.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract record fields from Airtable webhook payload",
        280,
        "You are a data mapper. Extract all fields from the Airtable webhook payload. Return: record_id, table_name, fields (object with all field name-value pairs), change_type (created or updated), changed_fields (array of field names that changed), and timestamp.",
        "Parse this Airtable webhook payload and extract all record fields: {webhook_payload}",
        [
          { field: "record_id", as: "airtable_record_id", type: "string" },
          { field: "table_name", as: "table_name", type: "string" },
          { field: "fields", as: "record_fields", type: "object" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "changed_fields", as: "changed_fields", type: "object" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Map Airtable fields to Trello card: title, description, list, labels, due date",
        520,
        {
          runCodeDetails: {
            code: `const fields = input.record_fields || {};
const title = fields["Name"] || fields["Title"] || fields["Task"] || "Untitled";
const description = fields["Description"] || fields["Notes"] || "";
const status = (fields["Status"] || "To Do").toLowerCase();
const priority = fields["Priority"] || "Medium";
const dueDate = fields["Due Date"] || null;
const listMap = { "to do": "{trello_todo_list_id}", "in progress": "{trello_inprogress_list_id}", "done": "{trello_done_list_id}", "review": "{trello_review_list_id}" };
const labelMap = { "High": "red", "Medium": "yellow", "Low": "green", "Urgent": "purple" };
const targetList = listMap[status] || listMap["to do"];
const labelColor = labelMap[priority] || "yellow";
return { card_name: title, card_desc: description + "\\n\\n---\\nAirtable ID: " + input.airtable_record_id, target_list_id: targetList, label_color: labelColor, due_date: dueDate, airtable_record_id: input.airtable_record_id, change_type: input.change_type };`,
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Trello API: Search for existing card by Airtable record ID",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.trello.com/1/search?query=Airtable ID: {airtable_record_id}&modelTypes=cards&board_id={trello_board_id}&key={trello_api_key}&token={trello_token}",
            headers: [],
            body: [],
            variables: [
              {
                name: "cards[0].id",
                customName: "existing_card_id",
                type: "string",
              },
              {
                name: "cards.length",
                customName: "card_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Card already exists in Trello?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "existing_card_id", operator: "is_not_empty", value: "" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Trello API: Create new card with mapped fields",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.trello.com/1/cards?key={trello_api_key}&token={trello_token}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "name", value: "{card_name}" },
              { key: "desc", value: "{card_desc}" },
              { key: "idList", value: "{target_list_id}" },
              { key: "due", value: "{due_date}" },
            ],
            variables: [
              { name: "id", customName: "new_card_id", type: "string" },
              { name: "url", customName: "card_url", type: "string" },
            ],
          },
        },
        300,
      ),
      n(
        "7",
        "hitApi",
        "Trello API: Update existing card with changed fields",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://api.trello.com/1/cards/{existing_card_id}?key={trello_api_key}&token={trello_token}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "name", value: "{card_name}" },
              { key: "desc", value: "{card_desc}" },
              { key: "idList", value: "{target_list_id}" },
              { key: "due", value: "{due_date}" },
            ],
            variables: [
              { name: "id", customName: "updated_card_id", type: "string" },
              { name: "url", customName: "card_url", type: "string" },
            ],
          },
        },
        -140,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
    ],
    summary:
      "Bidirectional Airtable to Trello sync. Receives Airtable record webhooks, AI-extracts record fields, maps them to Trello card properties (title, description, list by status, labels by priority, due dates), searches for existing cards by Airtable record ID, creates new cards or updates existing ones with changed fields to keep both systems in sync.",
  },

  "Airtable to Monday.com Sync": {
    nodes: [
      trigger("Webhook: Airtable record change detected", "webhook_app", {
        source: "airtable",
        event: "record.changed",
        howItStarts:
          "Fires when an Airtable record is created, updated, or deleted, sending the full record data and change metadata via webhook.",
        setup:
          "Configure Airtable automation to send webhooks on record changes. Set up the Monday.com board with matching column structure.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract record data and change type from payload",
        280,
        "You are a data extraction specialist. Parse the Airtable webhook payload and extract: record_id, change_type (created/updated/deleted), all field values with their Airtable field names, previous_values (for updates), and metadata (table name, view name, user who made the change).",
        "Parse this Airtable webhook payload: {webhook_payload}. Extract record data, change type, and metadata.",
        [
          { field: "record_id", as: "airtable_record_id", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "fields", as: "record_fields", type: "object" },
          { field: "previous_values", as: "previous_values", type: "object" },
          { field: "table_name", as: "source_table", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Transform data types to Monday.com column schema",
        520,
        {
          runCodeDetails: {
            code: `const fields = input.record_fields || {};
const columnMapping = {
  "Name": { monday_id: "name", type: "text" },
  "Status": { monday_id: "status", type: "status", valueMap: { "To Do": "Working on it", "In Progress": "Working on it", "Done": "Done", "Review": "Stuck" } },
  "Priority": { monday_id: "priority", type: "status", valueMap: { "High": "High", "Medium": "Medium", "Low": "Low" } },
  "Due Date": { monday_id: "date4", type: "date" },
  "Assignee": { monday_id: "person", type: "text" },
  "Description": { monday_id: "long_text", type: "text" },
};
const mondayValues = {};
Object.keys(fields).forEach(key => {
  const mapping = columnMapping[key];
  if (mapping) {
    let value = fields[key];
    if (mapping.type === "status" && mapping.valueMap) value = mapping.valueMap[value] || value;
    if (mapping.type === "date" && value) value = { date: value };
    mondayValues[mapping.monday_id] = value;
  }
});
return { monday_column_values: JSON.stringify(mondayValues), item_name: fields["Name"] || fields["Title"] || "Untitled", airtable_record_id: input.airtable_record_id, change_type: input.change_type };`,
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Monday.com API: Query for existing item by Airtable ID",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.monday.com/v2",
            headers: [
              { key: "Authorization", value: "Bearer {monday_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  '{ boards(ids: {monday_board_id}) { items_page(limit: 1, query_params: {rules: [{column_id: "text0", compare_value: ["{airtable_record_id}"]}]}) { items { id name } } } }',
              },
            ],
            variables: [
              {
                name: "data.boards[0].items_page.items[0].id",
                customName: "existing_item_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Item exists in Monday.com?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "existing_item_id", operator: "is_not_empty", value: "" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Monday.com API: Create new item with mapped columns",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.monday.com/v2",
            headers: [
              { key: "Authorization", value: "Bearer {monday_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  'mutation { create_item(board_id: {monday_board_id}, item_name: "{item_name}", column_values: "{monday_column_values}") { id } }',
              },
            ],
            variables: [
              {
                name: "data.create_item.id",
                customName: "new_item_id",
                type: "string",
              },
            ],
          },
        },
        300,
      ),
      n(
        "7",
        "hitApi",
        "Monday.com API: Update existing item with changed columns",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.monday.com/v2",
            headers: [
              { key: "Authorization", value: "Bearer {monday_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  'mutation { change_multiple_column_values(board_id: {monday_board_id}, item_id: {existing_item_id}, column_values: "{monday_column_values}") { id } }',
              },
            ],
            variables: [
              {
                name: "data.change_multiple_column_values.id",
                customName: "updated_item_id",
                type: "string",
              },
            ],
          },
        },
        -140,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
    ],
    summary:
      "Real-time Airtable to Monday.com synchronization. Receives Airtable record change webhooks, AI-extracts field data and change type, transforms Airtable field types to Monday.com column schema with value mapping, searches for existing items by Airtable record ID, and creates new items or updates existing ones to maintain bidirectional sync between the two platforms.",
  },

  "Asana Task Completion Notifier": {
    nodes: [
      trigger("Webhook: Asana task completed", "webhook_app", {
        source: "asana",
        event: "task.completed",
        howItStarts:
          "Fires when a task is marked as complete in Asana, delivering task metadata via webhook.",
        setup:
          "Configure Asana webhook subscription for task completion events on your target project. Use Asana's Events API or a webhook relay service.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract task details — name, project, assignee, custom fields",
        280,
        "You are an Asana data parser. Extract all task completion details from the webhook payload. Return: task_name, task_id, project_name, project_id, assignee_name, assignee_email, completed_at, due_date, was_on_time (boolean), custom_fields (object), tags (array), section_name, parent_task (if subtask), and task_url.",
        "Parse this Asana task completion webhook: {webhook_payload}. Extract all task details including custom fields.",
        [
          { field: "task_name", as: "task_name", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "assignee_name", as: "assignee_name", type: "string" },
          { field: "assignee_email", as: "assignee_email", type: "string" },
          { field: "was_on_time", as: "was_on_time", type: "boolean" },
          { field: "task_url", as: "task_url", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Determine notification recipients by project and team",
        520,
        {
          runCodeDetails: {
            code: `const project = input.project_name || "";
const assignee = input.assignee_name || "";
const projectTeams = {
  "Product Development": { slack_channel: "#product-updates", stakeholders: ["product-lead@company.com", "eng-lead@company.com"], cross_department: true },
  "Marketing": { slack_channel: "#marketing-updates", stakeholders: ["marketing-lead@company.com"], cross_department: false },
  "Design": { slack_channel: "#design-updates", stakeholders: ["design-lead@company.com", "product-lead@company.com"], cross_department: true },
  "Engineering": { slack_channel: "#eng-updates", stakeholders: ["eng-lead@company.com", "cto@company.com"], cross_department: true },
};
const teamConfig = projectTeams[project] || { slack_channel: "#general-updates", stakeholders: ["pm@company.com"], cross_department: false };
return { slack_channel: teamConfig.slack_channel, stakeholder_emails: teamConfig.stakeholders, is_cross_department: teamConfig.cross_department, notification_recipients: teamConfig.stakeholders.length, project_name: project, assignee_name: assignee };`,
          },
        },
      ),
      slackNode(
        "4",
        "Slack: Post formatted completion notification with task link",
        760,
        "✅ *Task Completed*\\n\\n*Task:* {task_name}\\n*Project:* {project_name}\\n*Completed by:* {assignee_name}\\n*On time:* {was_on_time}\\n*Link:* {task_url}\\n\\nChannel: {slack_channel}",
      ),
      n(
        "5",
        "condition",
        "Cross-department stakeholders need notification?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "is_cross_department",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Send completion notice to external stakeholders",
        1240,
        "{stakeholder_emails}",
        "Task Completed: {task_name} — {project_name}",
        "Hi,\\n\\nThis is a notification that the following task has been completed:\\n\\n• Task: {task_name}\\n• Project: {project_name}\\n• Completed by: {assignee_name}\\n• On time: {was_on_time}\\n\\nView the task: {task_url}\\n\\nThis update was sent because you are listed as a cross-department stakeholder for the {project_name} project.\\n\\nBest,\\nOmniEngage Task Notifier",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
    ],
    summary:
      "Asana task completion notification system. Receives task completion webhooks, AI-extracts full task details including custom fields and on-time status, determines notification recipients based on project and team configuration, posts formatted completion notifications to the appropriate Slack channel, and emails cross-department stakeholders when the task involves multiple teams.",
  },

  "ClickUp Task to Google Calendar": {
    nodes: [
      trigger(
        "Webhook: ClickUp task created or due date changed",
        "webhook_app",
        {
          source: "clickup",
          event: "taskUpdated",
          howItStarts:
            "Fires when a ClickUp task is created or its due date is changed, sending the task data via webhook.",
          setup:
            "Configure ClickUp webhook in your workspace settings to send task creation and update events to this workflow endpoint.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract task details — name, due date, assignee, priority",
        280,
        "You are a ClickUp data parser. Extract task details from the webhook payload. Return: task_id, task_name, due_date (ISO format), due_time (if set), assignee_name, assignee_email, priority (urgent/high/normal/low), estimated_duration_minutes, list_name, space_name, status, and task_url.",
        "Parse this ClickUp webhook payload: {webhook_payload}. Extract all task details including due date and priority.",
        [
          { field: "task_id", as: "clickup_task_id", type: "string" },
          { field: "task_name", as: "task_name", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "assignee_email", as: "assignee_email", type: "string" },
          { field: "priority", as: "task_priority", type: "string" },
          {
            field: "estimated_duration_minutes",
            as: "duration_minutes",
            type: "number",
          },
          { field: "task_url", as: "task_url", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Map task to calendar event with time slot heuristic",
        520,
        {
          runCodeDetails: {
            code: `const dueDate = input.due_date || new Date().toISOString();
const duration = input.duration_minutes || 60;
const priority = input.task_priority || "normal";
const taskName = input.task_name || "Untitled Task";
const priorityTimeMap = { urgent: "09:00", high: "10:00", normal: "14:00", low: "16:00" };
const startTime = priorityTimeMap[priority] || "14:00";
const dateOnly = dueDate.split("T")[0];
const startDateTime = dateOnly + "T" + startTime + ":00";
const endHour = parseInt(startTime.split(":")[0]) + Math.ceil(duration / 60);
const endMinute = duration % 60;
const endDateTime = dateOnly + "T" + String(endHour).padStart(2, "0") + ":" + String(endMinute).padStart(2, "0") + ":00";
const colorMap = { urgent: "11", high: "6", normal: "1", low: "2" };
return { event_summary: "[" + priority.toUpperCase() + "] " + taskName, event_description: "ClickUp Task: " + taskName + "\\nPriority: " + priority + "\\nLink: " + input.task_url + "\\nClickUp ID: " + input.clickup_task_id, start_datetime: startDateTime, end_datetime: endDateTime, color_id: colorMap[priority] || "1", attendee_email: input.assignee_email, clickup_task_id: input.clickup_task_id };`,
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Google Calendar API: Search for existing event by ClickUp task ID",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?q=ClickUp ID: {clickup_task_id}&maxResults=1&key={google_api_key}",
            headers: [
              { key: "Authorization", value: "Bearer {google_oauth_token}" },
            ],
            body: [],
            variables: [
              {
                name: "items[0].id",
                customName: "existing_event_id",
                type: "string",
              },
              {
                name: "items.length",
                customName: "event_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Calendar event already exists?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "existing_event_id", operator: "is_not_empty", value: "" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Google Calendar API: Create event with reminders",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?sendUpdates=all",
            headers: [
              { key: "Authorization", value: "Bearer {google_oauth_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "summary", value: "{event_summary}" },
              { key: "description", value: "{event_description}" },
              {
                key: "start",
                value:
                  '{"dateTime":"{start_datetime}","timeZone":"America/New_York"}',
              },
              {
                key: "end",
                value:
                  '{"dateTime":"{end_datetime}","timeZone":"America/New_York"}',
              },
              { key: "attendees", value: '[{"email":"{attendee_email}"}]' },
              { key: "colorId", value: "{color_id}" },
              {
                key: "reminders",
                value:
                  '{"useDefault":false,"overrides":[{"method":"popup","minutes":30},{"method":"email","minutes":60}]}',
              },
            ],
            variables: [
              { name: "id", customName: "new_event_id", type: "string" },
              { name: "htmlLink", customName: "event_link", type: "string" },
            ],
          },
        },
        300,
      ),
      n(
        "7",
        "hitApi",
        "Google Calendar API: Update existing event",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://www.googleapis.com/calendar/v3/calendars/primary/events/{existing_event_id}?sendUpdates=all",
            headers: [
              { key: "Authorization", value: "Bearer {google_oauth_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "summary", value: "{event_summary}" },
              { key: "description", value: "{event_description}" },
              {
                key: "start",
                value:
                  '{"dateTime":"{start_datetime}","timeZone":"America/New_York"}',
              },
              {
                key: "end",
                value:
                  '{"dateTime":"{end_datetime}","timeZone":"America/New_York"}',
              },
              { key: "colorId", value: "{color_id}" },
            ],
            variables: [
              { name: "id", customName: "updated_event_id", type: "string" },
              { name: "htmlLink", customName: "event_link", type: "string" },
            ],
          },
        },
        -140,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
    ],
    summary:
      "ClickUp task to Google Calendar sync. Receives ClickUp task webhooks, AI-extracts task details, maps tasks to calendar events using a priority-based time slot heuristic (urgent tasks get morning slots, low-priority gets afternoon), searches for existing events by ClickUp task ID, and creates new events with color-coded priority and reminders, or updates existing events when due dates change.",
  },

  "Gmail to Google Sheet Logger": {
    nodes: [
      trigger(
        "Webhook: New email matching Gmail filter received",
        "webhook_app",
        {
          source: "gmail",
          event: "message.received",
          howItStarts:
            "Fires when a new email matches a configured Gmail filter (e.g., invoices, order confirmations, specific senders).",
          setup:
            "Configure Gmail push notifications via Google Cloud Pub/Sub to send new message events to this workflow endpoint. Set up Gmail filters for the emails you want to capture.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract structured fields from email — sender, subject, key data",
        280,
        "You are an email data extractor. Parse the email and extract structured fields. Return: message_id, sender_email, sender_name, subject, received_date, email_type (invoice/order/receipt/notification/other), and key_entities (object with any extracted names, amounts, dates, reference numbers found in the email).",
        "Parse this email and extract structured data: From: {email_from}. Subject: {email_subject}. Body: {email_body}. Extract all key data points.",
        [
          { field: "message_id", as: "message_id", type: "string" },
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "subject", as: "email_subject", type: "string" },
          { field: "email_type", as: "email_type", type: "string" },
          { field: "key_entities", as: "key_entities", type: "object" },
          { field: "received_date", as: "received_date", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Parse email body for structured data: invoice amount, order number, dates",
        520,
        {
          runCodeDetails: {
            code: `const entities = input.key_entities || {};
const subject = input.email_subject || "";
const emailType = input.email_type || "other";
const invoiceAmount = entities.amount || entities.total || entities.invoice_amount || null;
const orderNumber = entities.order_number || entities.reference || entities.invoice_number || null;
const dueDate = entities.due_date || entities.delivery_date || null;
const currency = entities.currency || "USD";
const parsed = {
  invoice_amount: invoiceAmount ? parseFloat(String(invoiceAmount).replace(/[^0-9.]/g, "")) : null,
  order_number: orderNumber,
  due_date: dueDate,
  currency: currency,
  email_type: emailType,
  sender: input.sender_email,
  subject: subject,
  message_id: input.message_id,
  received_date: input.received_date,
  has_attachment: entities.has_attachment || false,
};
return { ...parsed, row_data: [parsed.received_date, parsed.sender, parsed.subject, parsed.email_type, parsed.order_number || "N/A", parsed.invoice_amount || "N/A", parsed.currency, parsed.due_date || "N/A", parsed.message_id] };`,
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Google Sheets API: Search for duplicate by message ID",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Email_Log!I:I",
            headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
            body: [],
            variables: [
              {
                name: "values",
                customName: "existing_message_ids",
                type: "object",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Duplicate email already logged?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_duplicate", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsNode(
        "6",
        "Google Sheets API: Append row with extracted data",
        1240,
        "Email_Log",
        '[["{received_date}","{sender}","{subject}","{email_type}","{order_number}","{invoice_amount}","{currency}","{due_date}","{message_id}"]]',
      ),
      n(
        "7",
        "hitApi",
        "Google Drive API: Save attachments and link in sheet",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "name", value: "{email_subject} — attachment" },
              { key: "parents", value: '["{google_drive_folder_id}"]' },
              { key: "mimeType", value: "application/octet-stream" },
            ],
            variables: [
              { name: "id", customName: "drive_file_id", type: "string" },
              {
                name: "webViewLink",
                customName: "attachment_link",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
    summary:
      "Gmail to Google Sheet logging pipeline. Captures filtered emails via webhook, AI-extracts structured fields (sender, subject, type), parses for specific data points (invoice amounts, order numbers, dates), deduplicates by message ID, appends extracted data to Google Sheets, and saves email attachments to Google Drive with linked references in the sheet.",
  },

  "Google Calendar to Notion Sync": {
    nodes: [
      trigger(
        "Webhook: Google Calendar event created or updated",
        "webhook_app",
        {
          source: "google_calendar",
          event: "event.updated",
          howItStarts:
            "Fires when a Google Calendar event is created or updated, sending the event data via webhook.",
          setup:
            "Configure Google Calendar push notifications via the Channel API to send event changes to this workflow endpoint.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract event details — title, time, attendees, location, Meet link",
        280,
        "You are a calendar data parser. Extract all event details from the Google Calendar webhook payload. Return: event_id, title, start_time (ISO), end_time (ISO), duration_minutes, attendees (array of emails), location, meet_link, description, organizer_email, is_recurring, recurrence_pattern, and calendar_name.",
        "Parse this Google Calendar webhook payload: {webhook_payload}. Extract all event details.",
        [
          { field: "event_id", as: "gcal_event_id", type: "string" },
          { field: "title", as: "event_title", type: "string" },
          { field: "start_time", as: "start_time", type: "string" },
          { field: "end_time", as: "end_time", type: "string" },
          { field: "attendees", as: "attendees", type: "object" },
          { field: "meet_link", as: "meet_link", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
        ],
      ),
      n("3", "runCode", "Map event properties to Notion database schema", 520, {
        runCodeDetails: {
          code: `const title = input.event_title || "Untitled Meeting";
const startTime = input.start_time || new Date().toISOString();
const endTime = input.end_time || new Date().toISOString();
const attendees = input.attendees || [];
const meetLink = input.meet_link || "";
const location = input.location || "";
const duration = input.duration_minutes || 30;
const notionProperties = {
  "Name": { title: [{ text: { content: title } }] },
  "Date": { date: { start: startTime, end: endTime } },
  "Attendees": { rich_text: [{ text: { content: attendees.map(a => a.email || a).join(", ") } }] },
  "Location": { rich_text: [{ text: { content: location || meetLink || "No location" } }] },
  "Duration": { number: duration },
  "Meet Link": { url: meetLink || null },
  "Calendar Event ID": { rich_text: [{ text: { content: input.gcal_event_id || "" } }] },
  "Status": { select: { name: "Scheduled" } },
};
const meetingNotesTemplate = "## Meeting Notes\\n\\n**Date:** " + startTime.split("T")[0] + "\\n**Attendees:** " + attendees.length + " people\\n**Duration:** " + duration + " min\\n\\n### Agenda\\n- \\n\\n### Notes\\n- \\n\\n### Action Items\\n- [ ] \\n";
return { notion_properties: notionProperties, meeting_notes_template: meetingNotesTemplate, gcal_event_id: input.gcal_event_id, event_title: title };`,
        },
      }),
      n(
        "4",
        "hitApi",
        "Notion API: Search for existing page by event ID",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/databases/{notion_database_id}/query",
            headers: [
              { key: "Authorization", value: "Bearer {notion_api_key}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filter",
                value:
                  '{"property":"Calendar Event ID","rich_text":{"equals":"{gcal_event_id}"}}',
              },
              { key: "page_size", value: "1" },
            ],
            variables: [
              {
                name: "results[0].id",
                customName: "existing_page_id",
                type: "string",
              },
              {
                name: "results.length",
                customName: "page_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Notion page already exists for this event?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "existing_page_id", operator: "is_not_empty", value: "" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Notion API: Create page with meeting notes template",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/pages",
            headers: [
              { key: "Authorization", value: "Bearer {notion_api_key}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "parent",
                value: '{"database_id":"{notion_database_id}"}',
              },
              { key: "properties", value: "{notion_properties}" },
              {
                key: "children",
                value:
                  '[{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Meeting Notes"}}]}},{"object":"block","type":"bulleted_list_item","bulleted_list_item":{"rich_text":[{"text":{"content":"Agenda items here..."}}]}},{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Action Items"}}]}},{"object":"block","type":"to_do","to_do":{"rich_text":[{"text":{"content":"Add action items..."}}],"checked":false}}]',
              },
            ],
            variables: [
              { name: "id", customName: "new_page_id", type: "string" },
              { name: "url", customName: "notion_page_url", type: "string" },
            ],
          },
        },
        300,
      ),
      n(
        "7",
        "hitApi",
        "Notion API: Update existing page properties",
        1240,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.notion.com/v1/pages/{existing_page_id}",
            headers: [
              { key: "Authorization", value: "Bearer {notion_api_key}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "properties", value: "{notion_properties}" }],
            variables: [
              { name: "id", customName: "updated_page_id", type: "string" },
              { name: "url", customName: "notion_page_url", type: "string" },
            ],
          },
        },
        -140,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
    ],
    summary:
      "Google Calendar to Notion bidirectional sync. Receives calendar event webhooks, AI-extracts event details (title, time, attendees, Meet link), maps to Notion database properties, searches for existing pages by Google Calendar event ID, creates new Notion pages with pre-populated meeting notes templates, or updates existing pages when event details change.",
  },

  "Google Sheet to Airtable Sync": {
    nodes: [
      trigger(
        "Webhook: Google Sheets row added or updated via Apps Script",
        "webhook_app",
        {
          source: "google_sheets",
          event: "row.changed",
          howItStarts:
            "Fires when a row is added or updated in Google Sheets, detected via Apps Script onChange trigger that sends a webhook.",
          setup:
            "Install a Google Apps Script on your sheet with an onChange trigger that sends the row data to this workflow's webhook endpoint.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract row data with header mapping",
        280,
        "You are a spreadsheet data parser. Extract the row data from the Google Sheets webhook payload. Map column values to their header names. Return: row_number, sheet_name, headers (array), row_values (object with header-name keys), change_type (new_row or updated_row), changed_columns (array for updates), and unique_key (first column or specified ID column value).",
        "Parse this Google Sheets webhook payload: {webhook_payload}. Map row values to column headers.",
        [
          { field: "row_number", as: "row_number", type: "number" },
          { field: "row_values", as: "row_values", type: "object" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "unique_key", as: "unique_key", type: "string" },
          { field: "headers", as: "column_headers", type: "object" },
        ],
      ),
      n("3", "runCode", "Transform sheet values to Airtable field types", 520, {
        runCodeDetails: {
          code: `const values = input.row_values || {};
const fieldMapping = {
  "Name": { airtable_field: "Name", type: "singleLineText" },
  "Email": { airtable_field: "Email", type: "email" },
  "Status": { airtable_field: "Status", type: "singleSelect" },
  "Priority": { airtable_field: "Priority", type: "singleSelect" },
  "Due Date": { airtable_field: "Due Date", type: "date" },
  "Amount": { airtable_field: "Amount", type: "number" },
  "Notes": { airtable_field: "Notes", type: "multilineText" },
  "Phone": { airtable_field: "Phone", type: "phoneNumber" },
};
const airtableFields = {};
Object.keys(values).forEach(header => {
  const mapping = fieldMapping[header];
  if (mapping) {
    let value = values[header];
    if (mapping.type === "number" && value) value = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
    if (mapping.type === "date" && value) {
      const d = new Date(value);
      value = isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
    }
    if (value !== null && value !== undefined && value !== "") airtableFields[mapping.airtable_field] = value;
  }
});
return { airtable_fields: airtableFields, unique_key: input.unique_key, field_count: Object.keys(airtableFields).length, change_type: input.change_type };`,
        },
      }),
      n("4", "hitApi", "Airtable API: Search for record by unique key", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.airtable.com/v0/{airtable_base_id}/{airtable_table_name}?filterByFormula={unique_key_field}='{unique_key}'&maxRecords=1",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "records[0].id",
              customName: "existing_record_id",
              type: "string",
            },
            {
              name: "records.length",
              customName: "record_count",
              type: "number",
            },
          ],
        },
      }),
      n("5", "condition", "Record exists in Airtable?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "existing_record_id",
              operator: "is_not_empty",
              value: "",
            },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Airtable API: Create new record",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.airtable.com/v0/{airtable_base_id}/{airtable_table_name}",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "fields", value: "{airtable_fields}" }],
            variables: [
              { name: "id", customName: "new_record_id", type: "string" },
            ],
          },
        },
        300,
      ),
      n(
        "7",
        "hitApi",
        "Airtable API: Update record with changed fields",
        1240,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.airtable.com/v0/{airtable_base_id}/{airtable_table_name}/{existing_record_id}",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "fields", value: "{airtable_fields}" }],
            variables: [
              { name: "id", customName: "updated_record_id", type: "string" },
            ],
          },
        },
        -140,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
    ],
    summary:
      "Google Sheets to Airtable sync connector. Receives row change webhooks from Apps Script, AI-extracts row data with header mapping, transforms Google Sheets values to Airtable field types (dates, numbers, selects), searches for existing records by unique key, and creates new records or updates existing ones with changed fields to keep both systems synchronized.",
  },

  "Notion Database to Google Sheet Sync": {
    nodes: [
      trigger(
        "Schedule: Every 30 minutes — sync Notion to Sheets",
        "schedule",
        {
          schedule: "*/30 * * * *",
          howItStarts:
            "Runs every 30 minutes to check for recently changed pages in the Notion database and sync them to Google Sheets.",
          setup:
            "Configure your Notion integration with access to the target database. Set up the Google Sheet with matching column headers.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Notion API: Fetch recently changed pages from database",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/databases/{notion_database_id}/query",
            headers: [
              { key: "Authorization", value: "Bearer {notion_api_key}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filter",
                value:
                  '{"timestamp":"last_edited_time","last_edited_time":{"past_hour":{}}}',
              },
              {
                key: "sorts",
                value:
                  '[{"timestamp":"last_edited_time","direction":"descending"}]',
              },
              { key: "page_size", value: "100" },
            ],
            variables: [
              { name: "results", customName: "notion_pages", type: "object" },
              {
                name: "has_more",
                customName: "has_more_pages",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Flatten Notion properties to sheet-safe values", 520, {
        runCodeDetails: {
          code: `const pages = input.notion_pages || [];
const flattenedRows = pages.map(page => {
  const props = page.properties || {};
  const row = { notion_page_id: page.id, last_edited: page.last_edited_time };
  Object.keys(props).forEach(key => {
    const prop = props[key];
    switch (prop.type) {
      case "title": row[key] = (prop.title || []).map(t => t.plain_text).join(""); break;
      case "rich_text": row[key] = (prop.rich_text || []).map(t => t.plain_text).join(""); break;
      case "number": row[key] = prop.number; break;
      case "select": row[key] = prop.select ? prop.select.name : ""; break;
      case "multi_select": row[key] = (prop.multi_select || []).map(s => s.name).join(", "); break;
      case "date": row[key] = prop.date ? prop.date.start : ""; break;
      case "checkbox": row[key] = prop.checkbox ? "Yes" : "No"; break;
      case "email": row[key] = prop.email || ""; break;
      case "url": row[key] = prop.url || ""; break;
      default: row[key] = JSON.stringify(prop[prop.type] || "");
    }
  });
  return row;
});
const headers = flattenedRows.length > 0 ? Object.keys(flattenedRows[0]) : [];
return { flattened_rows: flattenedRows, headers, row_count: flattenedRows.length, sync_time: new Date().toISOString() };`,
        },
      }),
      n(
        "4",
        "hitApi",
        "Google Sheets API: Search for existing rows by Notion page ID",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Notion_Sync!A:A",
            headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
            body: [],
            variables: [
              {
                name: "values",
                customName: "existing_page_ids",
                type: "object",
              },
            ],
          },
        },
      ),
      n("5", "runCode", "Determine create vs update operations", 1000, {
        runCodeDetails: {
          code: `const rows = input.flattened_rows || [];
const existingIds = (input.existing_page_ids || []).flat();
const headers = input.headers || [];
const toCreate = [];
const toUpdate = [];
rows.forEach((row, i) => {
  const pageId = row.notion_page_id;
  const rowValues = headers.map(h => String(row[h] || ""));
  const existingIndex = existingIds.indexOf(pageId);
  if (existingIndex >= 0) {
    toUpdate.push({ row_index: existingIndex + 1, values: rowValues, page_id: pageId });
  } else {
    toCreate.push({ values: rowValues, page_id: pageId });
  }
});
const createValues = toCreate.map(r => r.values);
const updateRequests = toUpdate.map(r => ({
  range: "Notion_Sync!A" + r.row_index + ":" + String.fromCharCode(64 + headers.length) + r.row_index,
  values: [r.values],
}));
return { create_values: createValues, update_requests: updateRequests, create_count: toCreate.length, update_count: toUpdate.length, total_synced: rows.length, headers };`,
        },
      }),
      n(
        "6",
        "hitApi",
        "Google Sheets API: Batch write new and updated rows",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values:batchUpdate",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "valueInputOption", value: "USER_ENTERED" },
              { key: "data", value: "{update_requests}" },
            ],
            variables: [
              {
                name: "totalUpdatedRows",
                customName: "updated_rows",
                type: "number",
              },
              {
                name: "totalUpdatedCells",
                customName: "updated_cells",
                type: "number",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "7",
        "Google Sheets: Add sync timestamp column",
        1480,
        "Sync_Log",
        '[["{new Date().toISOString()}","{create_count}","{update_count}","{total_synced}","{updated_cells}","success"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Scheduled Notion database to Google Sheets sync. Queries Notion for recently changed pages every 30 minutes, flattens complex Notion properties (rich text, selects, dates, checkboxes) into sheet-safe values, compares against existing sheet rows by page ID to determine create vs update operations, batch writes all changes to Google Sheets, and logs sync timestamps and operation counts.",
  },

  "Webflow Form to ActiveCampaign": {
    nodes: [
      trigger("Webhook: Webflow form submitted", "webhook_app", {
        source: "webflow",
        event: "form_submission",
        howItStarts:
          "Fires when a form is submitted on your Webflow site, sending all form data and metadata via webhook.",
        setup:
          "Configure Webflow webhook for form submission events in your site settings. Point it to this workflow's endpoint URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract form fields, consent flags, and UTM metadata",
        280,
        "You are a form data processor. Extract and normalize all form fields from the Webflow webhook payload. Return: email, first_name, last_name, phone, company, form_name, page_url, utm_source, utm_medium, utm_campaign, utm_content, utm_term, consent_marketing (boolean), consent_terms (boolean), submission_timestamp, and custom_fields (object with any additional form fields).",
        "Parse this Webflow form submission webhook: {webhook_payload}. Extract all form fields, consent flags, and UTM parameters.",
        [
          { field: "email", as: "contact_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "phone", as: "phone", type: "string" },
          { field: "form_name", as: "form_name", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          {
            field: "consent_marketing",
            as: "consent_marketing",
            type: "boolean",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Normalize contact data and map to ActiveCampaign schema",
        520,
        {
          runCodeDetails: {
            code: `const email = (input.contact_email || "").toLowerCase().trim();
const firstName = input.first_name || "";
const lastName = input.last_name || "";
const phone = (input.phone || "").replace(/[^0-9+]/g, "");
const formName = input.form_name || "unknown";
const consent = input.consent_marketing !== false;
const formToListMap = { "newsletter": 1, "contact": 2, "demo-request": 3, "download": 4, "webinar": 5 };
const formToTagMap = { "newsletter": "newsletter-subscriber", "contact": "contact-form-lead", "demo-request": "demo-request", "download": "content-download", "webinar": "webinar-registrant" };
const formKey = formName.toLowerCase().replace(/\\s+/g, "-");
const listId = formToListMap[formKey] || 2;
const tag = formToTagMap[formKey] || "webflow-lead";
const utmTag = input.utm_source ? "source-" + input.utm_source : "source-direct";
return { email, first_name: firstName, last_name: lastName, phone, list_id: listId, primary_tag: tag, utm_tag: utmTag, utm_source: input.utm_source || "direct", utm_campaign: input.utm_campaign || "none", has_consent: consent, form_name: formName };`,
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "ActiveCampaign API: Search for existing contact by email",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{activecampaign_account}.api-us1.com/api/3/contacts?email={email}",
            headers: [{ key: "Api-Token", value: "{activecampaign_api_key}" }],
            body: [],
            variables: [
              {
                name: "contacts[0].id",
                customName: "existing_contact_id",
                type: "string",
              },
              {
                name: "contacts.length",
                customName: "contact_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Contact already exists in ActiveCampaign?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "existing_contact_id",
              operator: "is_not_empty",
              value: "",
            },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "ActiveCampaign API: Create contact with tags and list assignment",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{activecampaign_account}.api-us1.com/api/3/contacts",
            headers: [
              { key: "Api-Token", value: "{activecampaign_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "contact",
                value:
                  '{"email":"{email}","firstName":"{first_name}","lastName":"{last_name}","phone":"{phone}","fieldValues":[{"field":"1","value":"{utm_source}"},{"field":"2","value":"{utm_campaign}"},{"field":"3","value":"{form_name}"}]}',
              },
            ],
            variables: [
              {
                name: "contact.id",
                customName: "new_contact_id",
                type: "string",
              },
            ],
          },
        },
        300,
      ),
      n(
        "7",
        "hitApi",
        "ActiveCampaign API: Update contact and trigger automation",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{activecampaign_account}.api-us1.com/api/3/contacts/{existing_contact_id}",
            headers: [
              { key: "Api-Token", value: "{activecampaign_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "contact",
                value:
                  '{"email":"{email}","firstName":"{first_name}","lastName":"{last_name}","phone":"{phone}","fieldValues":[{"field":"1","value":"{utm_source}"},{"field":"2","value":"{utm_campaign}"},{"field":"3","value":"{form_name}"}]}',
              },
            ],
            variables: [
              {
                name: "contact.id",
                customName: "updated_contact_id",
                type: "string",
              },
            ],
          },
        },
        -140,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
    ],
    summary:
      "Webflow form submission to ActiveCampaign CRM pipeline. Captures form submissions via webhook, AI-extracts all fields including consent flags and UTM parameters, normalizes data and maps to ActiveCampaign schema with form-specific list and tag assignments, searches for existing contacts by email, creates new contacts with tags and list assignment, or updates existing contacts and triggers re-engagement automations.",
  },
};
